<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ $t('taxPlanningMobileConfigurations') }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>
      <v-card class="bs" style="margin-top: 1rem">
        <div>
          <!-- Configuration Tabs -->
          <v-tabs v-model="activeTab" bg-color="transparent" color="black">
            <v-tab>{{ $t('mainConfiguration') }}</v-tab>
            <v-tab>{{ $t('suggestionFeature') }}</v-tab>
            <v-tab>{{ $t('suggestionQuestion') }}</v-tab>
            <v-tab>{{ $t('genieAICharacter') }}</v-tab>
            <v-tab>{{ $t('taxPrefix') }}</v-tab>
            <v-tab>{{ $t('taxDialog') }}</v-tab>
          </v-tabs>

          <v-window v-model="activeTab" :transition="false" :reverse-transition="false">

            <!-- Main Configuration Tab (Tax Planning only: Tax Planning Mode + Post Message) -->
            <v-window-item>
              <v-row style="padding: 25px" class="preview-split-row">
                <v-col cols="12" md="7" class="split-form-col">
                  <div v-if="loading">
                    <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                  </div>

                  <div v-else>
                    <!-- Tax Planning Mode -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">{{ $t('taxPlanningModeConfiguration') }}</h3>
                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">{{ $t('taxPlanningModeConfigurationDesc') }}</p>

                      <div class="language-switches">
                        <v-radio-group v-model="taxPlanning.mode" hide-details :disabled="!canEdit">
                          <v-radio value="maintenance" color="#4E3982">
                            <template #label>
                              <div class="tax-mode-option">
                                <span class="tax-mode-title">{{ $t('taxPlanningModeMaintenance') }}</span>
                                <span class="tax-mode-desc">{{ $t('taxPlanningModeMaintenanceDesc') }}</span>
                              </div>
                            </template>
                          </v-radio>
                          <v-radio value="enable" color="#4E3982">
                            <template #label>
                              <div class="tax-mode-option">
                                <span class="tax-mode-title">{{ $t('taxPlanningModeEnable') }}</span>
                                <span class="tax-mode-desc">{{ $t('taxPlanningModeEnableDesc') }}</span>
                              </div>
                            </template>
                          </v-radio>
                          <v-radio value="tryout" color="#4E3982">
                            <template #label>
                              <div class="tax-mode-option">
                                <span class="tax-mode-title">{{ $t('taxPlanningModeTryOut') }}</span>
                                <span class="tax-mode-desc">{{ $t('taxPlanningModeTryOutDesc') }}</span>
                              </div>
                            </template>
                          </v-radio>
                        </v-radio-group>

                        <div class="mt-4">
                          <CifListUploader
                            v-model="taxPlanning.whitelistCifFiles"
                            :label="$t('taxPlanningWhitelist')"
                            :hint="$t('taxPlanningWhitelistHint')"
                            :can-edit="canEdit"
                          />
                          <CifListUploader
                            v-model="taxPlanning.blacklistCifFiles"
                            :label="$t('taxPlanningBlacklist')"
                            :hint="$t('taxPlanningBlacklistHint')"
                            :can-edit="canEdit"
                          />
                          <CifListUploader
                            v-model="taxPlanning.availableCifFiles"
                            :label="$t('taxPlanningAvailableList')"
                            :hint="$t('taxPlanningAvailableListHint')"
                            :can-edit="canEdit"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Voice Function Configuration (tax-specific) -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">Voice Function Configuration</h3>
                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">Enable or disable voice function for chatbot</p>

                      <div class="language-switches">
                        <div class="language-option">
                          <v-switch inset v-model="config.voice.enabled" label="Enable Voice" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                        </div>

                        <div v-if="config.voice.enabled">
                          <div class="language-option">
                            <v-radio-group v-model="config.voice.type" row hide-details :disabled="!canEdit">
                              <v-radio value="tts" label="TTS" color="#4E3982"></v-radio>
                              <v-radio value="realTime" label="Real-Time" color="#4E3982"></v-radio>
                            </v-radio-group>
                          </div>

                          <div class="voice-system-prompt-section mt-3 ml-4" v-if="config.voice.textToSpeechType !== 'gemini'">
                            <label class="voice-prompt-label">Voice System Prompt</label>
                            <v-textarea
                              v-model="config.voice.systemPrompt"
                              placeholder="Input system prompt"
                              variant="plain"
                              density="compact"
                              rows="4"
                              :maxlength="10000"
                              hide-details="false"
                              class="voice-prompt-textarea"
                              :disabled="!canEdit"
                            ></v-textarea>
                            <div style="display: flex; justify-content: flex-end; width: 100%">
                              <span style="color: gray">{{ (config.voice.systemPrompt || '').length }}/10000</span>
                            </div>
                          </div>
                          <div class="voice-system-prompt-section mt-3 ml-4" v-else>
                            <label class="voice-prompt-label">Gemini Voice System Prompt</label>
                            <v-textarea
                              v-model="config.voice.geminiSystemPrompt"
                              placeholder="Input system prompt"
                              variant="plain"
                              density="compact"
                              rows="4"
                              :maxlength="10000"
                              hide-details="false"
                              class="voice-prompt-textarea"
                              :disabled="!canEdit"
                            ></v-textarea>
                            <div style="display: flex; justify-content: flex-end; width: 100%">
                              <span style="color: gray">{{ (config.voice.geminiSystemPrompt || '').length }}/10000</span>
                            </div>
                          </div>

                          <!-- Speech to Text / Text to Speech Configuration -->
                          <v-row class="mt-3 ml-4" no-gutters>
                            <v-col cols="6" class="d-flex align-center">
                              <label class="voice-prompt-label mr-2">Speech to Text</label>
                              <v-radio-group v-model="config.voice.speechToTextType" row hide-details :disabled="!canEdit" class="mt-0">
                                <v-radio value="device" label="Device" color="#4E3982"></v-radio>
                                <v-radio value="azure" label="Azure Speech" color="#4E3982"></v-radio>
                                <!-- App asks POST /api/v1/LHB/GetGoogleSTTV2TokenLHB for an access token, settings in Setup → Speech to Text -->
                                <v-radio value="googleV2" label="Google STT V2" color="#4E3982"></v-radio>
                              </v-radio-group>
                            </v-col>
                            <v-col cols="6" class="d-flex align-center">
                              <label class="voice-prompt-label mr-2">Text to Speech</label>
                              <v-radio-group v-model="config.voice.textToSpeechType" row hide-details :disabled="!canEdit" class="mt-0">
                                <v-radio value="default" label="Default" color="#4E3982"></v-radio>
                                <v-radio value="gemini" label="Gemini" color="#4E3982"></v-radio>
                              </v-radio-group>
                            </v-col>
                          </v-row>

                          <!-- Voice Config Selection -->
                          <v-row class="mt-3 ml-4" no-gutters>
                            <v-col cols="12">
                              <v-select
                                v-model="config.voice.voiceConfigurationId"
                                :items="voiceConfigOptions"
                                :item-title="voiceConfigItemTitle"
                                item-value="id"
                                label="Voice Config"
                                variant="outlined"
                                density="comfortable"
                                clearable
                                hide-details
                                class="modern-input"
                                style="max-width: 400px"
                                :disabled="!canEdit"
                              ></v-select>
                              <div class="mt-1" style="font-size: 13px; color: gray">
                                <span v-if="voiceConfigOptions.length">Which voice settings bundle this mode uses. Leave empty to use none.</span>
                                <span v-else>No voice configuration exists yet — create one in LH Bank Configuration › Voice Configs.</span>
                              </div>
                            </v-col>
                          </v-row>

                          <!-- End-of-speech Detection Configuration -->
                          <div class="language-option mt-3 ml-4">
                            <label class="voice-prompt-label">End-of-speech Detection (milliseconds)</label>
                            <v-text-field
                              v-model.number="config.voice.endOfSpeechDetection"
                              type="number"
                              placeholder="Enter timeout in milliseconds"
                              variant="outlined"
                              density="compact"
                              :rules="endOfSpeechDetectionRules"
                              class="modern-input"
                              style="max-width: 300px"
                              min="0"
                              @keypress="allowOnlyPositiveNumbers"
                              :disabled="!canEdit"
                            ></v-text-field>
                          </div>

                          <!-- TTS Configuration Fields (Hidden — set v-if to true to re-enable) -->
                          <template v-if="false">
                          <!-- Reformat For TTS Prompt -->
                          <div class="voice-system-prompt-section mt-3 ml-4">
                            <label class="voice-prompt-label">Reformat For TTS Prompt</label>
                            <v-textarea
                              v-model="config.voice.reformatForTTSPrompt"
                              placeholder="Enter prompt for reformatting text for TTS (spoken language)"
                              variant="plain"
                              density="compact"
                              rows="6"
                              :maxlength="10000"
                              hide-details="false"
                              class="voice-prompt-textarea"
                            ></v-textarea>
                            <div style="display: flex; justify-content: flex-end; width: 100%">
                              <span style="color: gray">{{ (config.voice.reformatForTTSPrompt || '').length }}/10000</span>
                            </div>
                          </div>

                          <!-- Format For TTS Engine Prompt -->
                          <div class="voice-system-prompt-section mt-3 ml-4">
                            <label class="voice-prompt-label">Format For TTS Engine Prompt (SSML)</label>
                            <v-textarea
                              v-model="config.voice.formatForTTSEnginePrompt"
                              placeholder="Enter prompt for formatting text with SSML for TTS engine"
                              variant="plain"
                              density="compact"
                              rows="6"
                              :maxlength="10000"
                              hide-details="false"
                              class="voice-prompt-textarea"
                            ></v-textarea>
                            <div style="display: flex; justify-content: flex-end; width: 100%">
                              <span style="color: gray">{{ (config.voice.formatForTTSEnginePrompt || '').length }}/10000</span>
                            </div>
                          </div>

                          <!-- TTS Model Configuration -->
                          <div class="language-option mt-3 ml-4">
                            <label class="voice-prompt-label">TTS Model</label>
                            <v-text-field
                              v-model="config.voice.ttsModel"
                              placeholder="e.g., gpt-5-chat, gpt-4o"
                              variant="outlined"
                              density="compact"
                              class="modern-input"
                              style="max-width: 300px"
                              hide-details
                            ></v-text-field>
                          </div>

                          <!-- TTS API Version Configuration -->
                          <div class="language-option mt-3 ml-4">
                            <label class="voice-prompt-label">TTS API Version</label>
                            <v-text-field
                              v-model="config.voice.ttsApiVersion"
                              placeholder="e.g., 2025-01-01-preview"
                              variant="outlined"
                              density="compact"
                              class="modern-input"
                              style="max-width: 300px"
                              hide-details
                            ></v-text-field>
                          </div>
                          </template>
                        </div>
                      </div>
                    </div>

                    <!-- Post Message Configuration (tax-specific) -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">{{ $t('postMessageConfiguration') }}</h3>

                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">{{ $t('enableDisablePostMessage') }}</p>

                      <div class="language-switches">
                        <div class="language-option">
                          <v-switch inset v-model="config.postMessage.enabled" :label="$t('enable')" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                        </div>
                        <v-row>
                          <v-col cols="2" class="postMsg">
                            <span style="font-size: 18px">Thai (default)</span>
                          </v-col>
                          <v-col cols="10" class="postMsg">
                            <v-text-field
                              v-model="postMessageText"
                              :disabled="!config.postMessage.enabled || !canEdit"
                              placeholder="Input post message in Thai"
                              hide-details
                              variant="outlined"
                              rounded
                              class="search-field ml-3 mr-3"
                              :maxlength="500"
                              counter="500"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="2" class="postMsg">
                            <span style="font-size: 18px">English</span>
                          </v-col>
                          <v-col cols="10" class="postMsg">
                            <v-text-field
                              v-model="postMessageTextEN"
                              :disabled="!config.postMessage.enabled || !canEdit"
                              placeholder="Input post message in English"
                              hide-details
                              variant="outlined"
                              rounded
                              class="search-field ml-3 mr-3"
                              :maxlength="500"
                              counter="500"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="2" class="postMsg">
                            <span style="font-size: 18px">Chinese</span>
                          </v-col>
                          <v-col cols="10" class="postMsg">
                            <v-text-field
                              v-model="postMessageTextCN"
                              :disabled="!config.postMessage.enabled || !canEdit"
                              placeholder="Input post message in Chinese"
                              hide-details
                              variant="outlined"
                              rounded
                              class="search-field ml-3 mr-3"
                              :maxlength="500"
                              counter="500"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="2" class="postMsg">
                            <span style="font-size: 18px">{{ $t('postMessageColor') }}</span>
                          </v-col>
                          <v-col cols="10" class="postMsg">
                            <PostMessageColorField
                              v-model="postMessageColor"
                              :disabled="!config.postMessage.enabled || !canEdit"
                              :fallback-color="postMessageDefaultColor"
                            />
                          </v-col>
                        </v-row>
                      </div>
                    </div>

                    <!-- App Title Configuration (tax-specific) -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">App Title</h3>
                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">Enter the app title shown in mobile header</p>

                      <div class="language-switches">
                        <v-text-field
                          v-model="appTitle"
                          placeholder="e.g., Genie"
                          variant="outlined"
                          density="compact"
                          :maxlength="100"
                          counter
                          hide-details="auto"
                          class="modern-input"
                          :disabled="!canEdit"
                        ></v-text-field>
                      </div>
                    </div>

                    <!-- Mobile Information Configuration (tax-specific) -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">Mobile Information</h3>
                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">Enter additional information for mobile application</p>

                      <div class="language-switches">
                        <label class="field-label">Header (Optional)</label>
                        <v-text-field
                          v-model="mobileInformationHeader"
                          placeholder="Enter section header (optional)"
                          variant="outlined"
                          density="compact"
                          :maxlength="50"
                          counter
                          hide-details="auto"
                          class="modern-input mb-3"
                          :disabled="!canEdit"
                        ></v-text-field>

                        <label class="field-label">Information (Thai)</label>
                        <v-textarea
                          v-model="mobileInformation"
                          placeholder="Enter mobile information in Thai"
                          variant="outlined"
                          density="compact"
                          rows="10"
                          :maxlength="4000"
                          counter
                          class="modern-input mb-3"
                          :disabled="!canEdit"
                        ></v-textarea>

                        <label class="field-label">Information (English)</label>
                        <v-textarea
                          v-model="mobileInformationEN"
                          placeholder="Enter mobile information in English"
                          variant="outlined"
                          density="compact"
                          rows="10"
                          :maxlength="4000"
                          counter
                          class="modern-input"
                          :disabled="!canEdit"
                        ></v-textarea>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Preview Column -->
                <v-col cols="12" md="5" class="split-preview-col">
                  <div class="mobile-preview-sticky">
                    <div style="background-color: #f3f3f3; border-radius: 14px; padding: 24px 0" class="d-flex justify-center align-center">
                      <v-skeleton-loader v-if="loading" type="image" width="268" height="582" class="mobile-skeleton"></v-skeleton-loader>
                      <MobileTaxPlannerPreview
                        v-else
                        :features="suggestionConfig.features"
                        :questions="suggestionQuestionConfig.questions"
                        :config="config"
                        :enabled-languages="enabledLanguages"
                        :post-message-text="postMessageText"
                        :post-message-color="postMessagePreviewColor"
                        :suggest-bar-text="suggestBarText || suggestBarTextEN"
                        :genie-icon-url="genieChatIconUrl"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-window-item>

            <!-- Suggestion Feature Tab -->
            <v-window-item>
              <v-row style="padding: 25px" class="preview-split-row">
                <v-col cols="12" md="7" class="split-form-col">
                  <!-- Loading Skeleton for Suggestion Features -->
                  <div v-if="loading">
                    <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                  </div>

                  <!-- Suggestion Configuration Content (only show when not loading) -->
                  <div v-else class="mb-4">
                    <!-- Suggest Bar — ข้อความหัวแถบที่โชว์เหนือปุ่ม suggestion บนมือถือ · เว้นว่าง = ไม่แสดงแถบ -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">{{ $t('suggestBar') }}</h3>
                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">{{ $t('suggestBarDescription') }}</p>
                      <v-row>
                        <v-col cols="4">
                          <div class="d-flex align-center mb-2"><span style="font-size: 16px; font-weight: 500">In Thai</span></div>
                          <input v-model="suggestBarText" placeholder="Suggest bar text" class="custom-input" maxlength="500" :disabled="!canEdit" />
                        </v-col>
                        <v-col cols="4">
                          <div class="d-flex align-center mb-2"><span style="font-size: 16px; font-weight: 500">In English</span></div>
                          <input v-model="suggestBarTextEN" placeholder="Suggest bar text" class="custom-input" maxlength="500" :disabled="!canEdit" />
                        </v-col>
                        <v-col cols="4">
                          <div class="d-flex align-center mb-2"><span style="font-size: 16px; font-weight: 500">In Chinese</span></div>
                          <input v-model="suggestBarTextCN" placeholder="Suggest bar text" class="custom-input" maxlength="500" :disabled="!canEdit" />
                        </v-col>
                      </v-row>
                    </div>
                    <!-- Tax History Title — หัวข้อที่แสดงบนจอประวัติภาษีในแอป (เปิดจาก action OPEN_TAX_HISTORY) -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <h3 class="language-config-title">{{ $t('taxHistoryTitle') }}</h3>
                        <div style="flex-grow: 1" class="gradient-bar ml-2"></div>
                      </div>
                      <p class="language-config-subtitle">{{ $t('taxHistoryTitleDescription') }}</p>
                      <v-row>
                        <v-col cols="4">
                          <div class="d-flex align-center mb-2"><span style="font-size: 16px; font-weight: 500">In Thai</span></div>
                          <input v-model="taxHistoryTitle" placeholder="Tax history title" class="custom-input" maxlength="500" :disabled="!canEdit" />
                        </v-col>
                        <v-col cols="4">
                          <div class="d-flex align-center mb-2"><span style="font-size: 16px; font-weight: 500">In English</span></div>
                          <input v-model="taxHistoryTitleEN" placeholder="Tax history title" class="custom-input" maxlength="500" :disabled="!canEdit" />
                        </v-col>
                        <v-col cols="4">
                          <div class="d-flex align-center mb-2"><span style="font-size: 16px; font-weight: 500">In Chinese</span></div>
                          <input v-model="taxHistoryTitleCN" placeholder="Tax history title" class="custom-input" maxlength="500" :disabled="!canEdit" />
                        </v-col>
                      </v-row>
                    </div>
                    <div class="d-flex start align-center mb-3">
                      <h3 class="language-config-title mr-3">{{ $t('suggestionFeature') }}</h3>
                      <v-switch inset class="ma-0 ml-3" v-model="suggestionConfig.enabled" :label="$t('enable')" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                    </div>

                    <div v-if="suggestionConfig.enabled" class="suggestion-form">
                      <v-row>
                        <v-col cols="6">
                          <!-- Add New Feature Button -->
                          <div class="mb-3">
                            <v-btn color="#4E3982" variant="outlined" style="height: 40px; border-radius: 12px; text-transform: none" @click="addNewFeature" :disabled="!canAdd">
                              <v-icon start size="small">mdi-plus</v-icon>
                              Add more feature
                            </v-btn>
                            <span class="ml-2 text-caption">{{ suggestionConfig.features.length }} Feature(s)</span>
                          </div>
                        </v-col>
                        <v-col cols="6" class="d-flex justify-end align-center">
                          <p class="language-config-subtitle" style="font-size: 16px">*Only 4 features will be shown at a time.</p>
                        </v-col>
                      </v-row>

                      <!-- Dynamic Features -->
                      <div v-for="(feature, index) in suggestionConfig.features" :key="index" class="feature-section mb-4 pa-4" style="border: 1px solid #e0e0e0; border-radius: 8px">
                        <div class="d-flex align-center mb-2">
                          <h4>Feature {{ index + 1 }}</h4>
                          <div style="flex-grow: 1; background-color: #000000; opacity: 20%; height: 1px" class="mx-2"></div>

                          <!-- Remove Feature Button -->
                          <v-btn v-if="suggestionConfig.features.length > 1" icon size="small" variant="plain" color="error" @click="removeFeature(feature, index)" class="mr-2">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>

                          <!-- Collapse/Expand Button -->
                          <v-btn icon size="small" variant="plain" @click="feature.collapsed = !feature.collapsed">
                            <v-icon size="28">{{ feature.collapsed ? 'mdi-menu-down' : 'mdi-menu-up' }}</v-icon>
                          </v-btn>
                        </div>

                        <div v-if="!feature.collapsed">
                          <!-- Language Messages -->
                          <div class="mb-3">
                            <v-row>
                              <v-col cols="2">
                                <div class="mb-2" style="display: flex; justify-content: center">
                                  <!-- Dynamic Image with Placeholder -->
                                  <div class="SuggestFeatureIcon">
                                    <v-img
                                      v-if="getFeatureImageUrl(feature, index)"
                                      :src="getFeatureImageUrl(feature, index)"
                                      width="80"
                                      height="80"
                                      class="mx-auto"
                                      style="border-radius: 8px"
                                      @error="onImageError(feature, index)"
                                      @click="canEdit ? openImageUpload(index) : null"
                                      :style="!canEdit ? 'cursor: not-allowed; opacity: 0.6' : 'cursor: pointer'"
                                    >
                                      <template v-slot:placeholder>
                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                          <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
                                        </v-row>
                                      </template>
                                      <template v-slot:error>
                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                          <v-icon color="grey-lighten-3" size="32">mdi-image-broken-variant</v-icon>
                                        </v-row>
                                      </template>
                                    </v-img>
                                    <!-- Plain square box placeholder when no image -->
                                    <div
                                      v-else
                                      @click="canEdit ? openImageUpload(index) : null"
                                      :style="{ cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }"
                                      style="
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        width: 80px;
                                        height: 80px;
                                        background-color: transparent;
                                        border: 2px dashed #ccc;
                                        border-radius: 8px;
                                      "
                                    >
                                      <v-icon color="#eee" size="32">mdi-image-plus</v-icon>
                                    </div>
                                  </div>
                                </div>
                              </v-col>
                              <v-col cols="10">
                                <v-row>
                                  <v-col cols="4">
                                    <div class="d-flex align-center mb-2">
                                      <span style="font-size: 16px; font-weight: 500">In Thai</span>
                                    </div>
                                    <input v-model="feature.messages.thai" placeholder="Message" class="custom-input" :disabled="!canEdit" />
                                  </v-col>
                                  <v-col cols="4">
                                    <div class="d-flex align-center mb-2">
                                      <span style="font-size: 16px; font-weight: 500">In English</span>
                                    </div>
                                    <input v-model="feature.messages.english" placeholder="Message" class="custom-input" :disabled="!canEdit" />
                                  </v-col>
                                  <v-col cols="4">
                                    <div class="d-flex align-center mb-2">
                                      <span style="font-size: 16px; font-weight: 500">In Chinese</span>
                                    </div>
                                    <input v-model="feature.messages.chinese" placeholder="Message" class="custom-input" :disabled="!canEdit" />
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </div>

                          <!-- Model Name -->
                          <!-- <div class="mb-3">
                            <label class="form-label">Model name</label>
                            <v-select v-model="feature.modelName" :items="['1', '2', '3', '4', '5']" variant="outlined" density="compact" hide-details></v-select>
                          </div> -->

                          <!-- Target Group and Priority -->
                          <v-row class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Target Group</label>
                              <div>
                                <v-radio-group v-model="feature.targetGroup" inline hide-details :disabled="!canEdit">
                                  <v-radio value="all" label="All" color="#4E3982" style="margin-right: 35px"></v-radio>
                                  <v-radio value="persona" label="Persona" color="#4E3982" style="margin-right: 35px"></v-radio>
                                  <v-radio value="cif-list" label="Cif List" color="#4E3982"></v-radio>
                                </v-radio-group>
                                <div v-if="feature.targetGroup === 'cif-list'" class="mt-2">
                                  <v-btn variant="outlined" size="small" color="#4E3982" style="margin-right: 8px" @click="triggerFeatureCifUpload(index)" :disabled="!canEdit">
                                    <v-icon size="16" class="mr-1">mdi-upload</v-icon>
                                    Upload File
                                  </v-btn>
                                  <v-btn variant="outlined" size="small" color="#4E3982" @click="downloadCifTemplate">
                                    <v-icon size="16" class="mr-1">mdi-download</v-icon>
                                    Download Template
                                  </v-btn>
                                </div>
                              </div>
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Priority</label>
                              <input
                                v-model="feature.priority"
                                placeholder="Number (0 is most important)"
                                class="custom-input"
                                @input="validatePriority($event, feature)"
                                type="text"
                                pattern="[0-9]*"
                                :disabled="!canEdit"
                              />
                            </v-col>
                          </v-row>

                          <!-- CIF Files Display -->
                          <div v-if="feature.targetGroup === 'cif-list' && feature.cifFiles && feature.cifFiles.length > 0" class="mb-3">
                            <v-card variant="outlined" class="pa-3">
                              <div class="d-flex align-center justify-space-between mb-2">
                                <div>
                                  <v-icon class="mr-2" color="primary" size="20">mdi-file-table</v-icon>
                                  <span style="font-size: 14px; font-weight: 500">Uploaded CIF Files ({{ feature.cifFiles.length }}) - Total CIF Codes: {{ getTotalFeatureCifCodes(feature) }}</span>
                                </div>
                              </div>
                              <v-table density="compact" class="modern-table">
                                <thead>
                                  <tr>
                                    <th class="text-left">File Name</th>
                                    <th class="text-center" style="width: 100px">CIF Count</th>
                                    <th class="text-center" style="width: 120px">Upload Date</th>
                                    <th class="text-center" style="width: 180px">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(file, fileIndex) in feature.cifFiles" :key="fileIndex">
                                    <td class="py-2">
                                      <div class="d-flex align-center">
                                        <v-icon class="mr-2" color="success" size="16">mdi-file-document</v-icon>
                                        <div>
                                          <div style="font-size: 13px">{{ file.fileName }}</div>
                                          <div style="font-size: 11px; color: #666">{{ formatFileSize(file.fileSize) }}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td class="text-center py-2">
                                      <v-chip size="x-small" color="primary" variant="outlined">
                                        {{ file.cifList ? file.cifList.length : 0 }}
                                      </v-chip>
                                    </td>
                                    <td class="text-center py-2" style="font-size: 11px">
                                      {{ formatUploadDate(file.uploadDate) }}
                                    </td>
                                    <td class="text-center py-2">
                                      <v-btn variant="outlined" color="primary" size="x-small" class="mr-1" @click="downloadFeatureCifFile(file)">
                                        <v-icon size="14">mdi-download</v-icon>
                                      </v-btn>
                                      <v-btn variant="outlined" color="error" size="x-small" @click="removeFeatureCifFile(index, fileIndex)">
                                        <v-icon size="14">mdi-delete</v-icon>
                                      </v-btn>
                                    </td>
                                  </tr>
                                </tbody>
                              </v-table>
                              <div v-if="feature.cifFiles.length > 1" class="d-flex justify-end mt-2">
                                <v-btn variant="outlined" color="error" size="x-small" @click="clearAllFeatureCifFiles(index)">
                                  <v-icon size="14" class="mr-1">mdi-delete-sweep</v-icon>
                                  Clear All
                                </v-btn>
                              </div>
                            </v-card>
                          </div>

                          <!-- Segment and Wealth Membership show when targetGroup is 'persona') -->
                          <v-row v-if="feature.targetGroup === 'persona'" class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Segment</label>
                              <v-select
                                v-model="feature.segment"
                                :items="segmentOptions"
                                item-title="name"
                                item-value="valueString"
                                variant="solo"
                                density="compact"
                                multiple
                                chips
                                class="custom-select"
                                hide-details
                                flat
                                :disabled="!canEdit"
                              >
                              </v-select>
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Wealth Membership</label>
                              <v-select
                                v-model="feature.wealthMembership"
                                :items="wealthMembershipOptions"
                                item-title="name"
                                item-value="valueString"
                                variant="solo"
                                density="compact"
                                multiple
                                chips
                                class="custom-select"
                                hide-details
                                flat
                                :disabled="!canEdit"
                              ></v-select>
                            </v-col>
                          </v-row>

                          <!-- Action -->
                          <v-row class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">{{ $t('action') }}</label>
                              <v-select
                                :model-value="feature.action"
                                @update:model-value="(val) => handleActionSelect(feature, val)"
                                :items="actionSelectItems"
                                item-title="name"
                                item-value="name"
                                variant="solo"
                                density="compact"
                                class="custom-select"
                                hide-details
                                flat
                                clearable
                                :disabled="!canEdit"
                              >
                                <template v-slot:item="{ props, item }">
                                  <template v-if="item.raw.__isAddAction">
                                    <v-divider class="my-1"></v-divider>
                                    <v-list-item v-bind="props" prepend-icon="mdi-plus-circle-outline" :title="$t('addAction')"></v-list-item>
                                  </template>
                                  <v-list-item v-else v-bind="props"></v-list-item>
                                </template>
                              </v-select>
                            </v-col>
                          </v-row>

                          <!-- Effective Dates -->
                          <v-row class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Effective Start Date</label>
                              <input v-model="feature.effectiveStartDate" type="date" class="custom-input" :disabled="!canEdit" />
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Effective End Date</label>
                              <input
                                v-model="feature.effectiveEndDate"
                                type="date"
                                class="custom-input"
                                :class="{ 'input-error': !validateDateRange(feature.effectiveStartDate, feature.effectiveEndDate) }"
                                :disabled="!canEdit"
                              />
                              <div v-if="!validateDateRange(feature.effectiveStartDate, feature.effectiveEndDate)" class="error-message">End Date must be on or after Start Date</div>
                            </v-col>
                          </v-row>

                          <!-- Upload and Enable -->
                          <div class="d-flex align-center justify-start">
                            <v-btn color="#4E3982" size="small" style="border-radius: 12px; height: 40px; padding: 0 25px; margin-right: 25px" @click="openImageUpload(index)" :disabled="!canEdit">
                              <v-icon start size="small">mdi-upload</v-icon>
                              Upload
                            </v-btn>
                            <v-switch inset class="ma-0 ml-3" v-model="feature.featureEnabled" label="Enable" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="5" class="split-preview-col">
                  <div class="mobile-preview-sticky">
                    <div style="background-color: #f3f3f3; border-radius: 14px; padding: 24px 0" class="d-flex justify-center align-center">
                      <v-skeleton-loader v-if="loading" type="image" width="268" height="582" class="mobile-skeleton"></v-skeleton-loader>
                      <MobileTaxPlannerPreview
                        v-else
                        :features="suggestionConfig.features"
                        :questions="suggestionQuestionConfig.questions"
                        :config="config"
                        :enabled-languages="enabledLanguages"
                        :post-message-text="postMessageText"
                        :post-message-color="postMessagePreviewColor"
                        :suggest-bar-text="suggestBarText || suggestBarTextEN"
                        :genie-icon-url="genieChatIconUrl"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-window-item>

            <!-- Suggestion Question Tab -->
            <v-window-item>
              <v-row style="padding: 25px" class="preview-split-row">
                <v-col cols="12" md="7" class="split-form-col">
                  <!-- Loading Skeleton for Suggestion Questions -->
                  <div v-if="loading">
                    <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                  </div>

                  <!-- Suggestion Question Configuration Content (only show when not loading) -->
                  <div v-else class="mb-4">
                    <div class="d-flex start align-center mb-3">
                      <h3 class="language-config-title mr-3">{{ $t('suggestionQuestion') }}</h3>
                      <v-switch inset class="ma-0 ml-3" v-model="suggestionQuestionConfig.enabled" :label="$t('enable')" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                    </div>
                    <div v-if="suggestionQuestionConfig.enabled" class="suggestion-form">
                      <v-row>
                        <v-col cols="6">
                          <!-- Add New Question Button -->
                          <div class="mb-3">
                            <v-btn style="height: 40px; border-radius: 12px; text-transform: none" color="#4E3982" variant="outlined" @click="addNewQuestion" :disabled="!canAdd">
                              <v-icon start size="small">mdi-plus</v-icon>
                              Add new question
                            </v-btn>
                            <span class="ml-2 text-caption">{{ suggestionQuestionConfig.questions.length }} Question(s)</span>
                          </div>
                        </v-col>
                        <v-col cols="6" class="d-flex justify-end align-center">
                          <p class="language-config-subtitle" style="font-size: 16px">*Only 4 questions will be shown at a time.</p>
                        </v-col>
                      </v-row>

                      <!-- Dynamic Questions -->
                      <div v-for="(question, index) in suggestionQuestionConfig.questions" :key="index" class="feature-section mb-4 pa-4" style="border: 1px solid #e0e0e0; border-radius: 8px">
                        <div class="d-flex align-center mb-2">
                          <h4>Question {{ index + 1 }}</h4>
                          <div style="flex-grow: 1; background-color: #000000; opacity: 20%; height: 1px" class="mx-2"></div>

                          <!-- Remove Question Button -->
                          <v-btn v-if="suggestionQuestionConfig.questions.length > 1" icon size="small" variant="plain" color="error" @click="removeQuestion(index)" class="mr-2">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>

                          <!-- Collapse/Expand Button -->
                          <v-btn icon size="small" variant="plain" @click="question.collapsed = !question.collapsed">
                            <v-icon size="28">{{ question.collapsed ? 'mdi-menu-down' : 'mdi-menu-up' }}</v-icon>
                          </v-btn>
                        </div>

                        <div v-if="!question.collapsed">
                          <!-- Language Messages -->
                          <div class="mb-3">
                            <v-row>
                              <v-col cols="12">
                                <div class="d-flex align-center mb-2">
                                  <span style="font-size: 16px; font-weight: 500">In Thai</span>
                                </div>
                                <input v-model="question.messages.thai" placeholder="Message" class="custom-input" :disabled="!canEdit" />
                              </v-col>
                              <v-col cols="12">
                                <div class="d-flex align-center mb-2">
                                  <span style="font-size: 16px; font-weight: 500">In English</span>
                                </div>
                                <input v-model="question.messages.english" placeholder="Message" class="custom-input" :disabled="!canEdit" />
                              </v-col>
                              <v-col cols="12">
                                <div class="d-flex align-center mb-2">
                                  <span style="font-size: 16px; font-weight: 500">In Chinese</span>
                                </div>
                                <input v-model="question.messages.chinese" placeholder="Message" class="custom-input" :disabled="!canEdit" />
                              </v-col>
                            </v-row>
                          </div>

                          <!-- Target Group and Priority -->
                          <v-row class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Target Group</label>
                              <div>
                                <v-radio-group v-model="question.targetGroup" inline hide-details :disabled="!canEdit">
                                  <v-radio value="all" label="All" color="#4E3982" style="margin-right: 35px"></v-radio>
                                  <v-radio value="persona" label="Persona" color="#4E3982" style="margin-right: 35px"></v-radio>
                                  <v-radio value="cif-list" label="Cif List" color="#4E3982"></v-radio>
                                </v-radio-group>
                                <div v-if="question.targetGroup === 'cif-list'" class="mt-2">
                                  <v-btn variant="outlined" size="small" color="#4E3982" style="margin-right: 8px" @click="triggerQuestionCifUpload(index)" :disabled="!canEdit">
                                    <v-icon size="16" class="mr-1">mdi-upload</v-icon>
                                    Upload File
                                  </v-btn>
                                  <v-btn variant="outlined" size="small" color="#4E3982" @click="downloadCifTemplate">
                                    <v-icon size="16" class="mr-1">mdi-download</v-icon>
                                    Download Template
                                  </v-btn>
                                </div>
                              </div>
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Priority</label>
                              <input
                                v-model="question.priority"
                                placeholder="Number (0 is most important)"
                                class="custom-input"
                                @input="validatePriority($event, question)"
                                type="text"
                                pattern="[0-9]*"
                                :disabled="!canEdit"
                              />
                            </v-col>
                          </v-row>

                          <!-- CIF Files Display for Questions -->
                          <div v-if="question.targetGroup === 'cif-list' && question.cifFiles && question.cifFiles.length > 0" class="mb-3">
                            <v-card variant="outlined" class="pa-3">
                              <div class="d-flex align-center justify-space-between mb-2">
                                <div>
                                  <v-icon class="mr-2" color="primary" size="20">mdi-file-table</v-icon>
                                  <span style="font-size: 14px; font-weight: 500">Uploaded CIF Files ({{ question.cifFiles.length }}) - Total CIF Codes: {{ getTotalQuestionCifCodes(question) }}</span>
                                </div>
                              </div>
                              <v-table density="compact" class="modern-table">
                                <thead>
                                  <tr>
                                    <th class="text-left">File Name</th>
                                    <th class="text-center" style="width: 100px">CIF Count</th>
                                    <th class="text-center" style="width: 120px">Upload Date</th>
                                    <th class="text-center" style="width: 180px">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(file, fileIndex) in question.cifFiles" :key="fileIndex">
                                    <td class="py-2">
                                      <div class="d-flex align-center">
                                        <v-icon class="mr-2" color="success" size="16">mdi-file-document</v-icon>
                                        <div>
                                          <div style="font-size: 13px">{{ file.fileName }}</div>
                                          <div style="font-size: 11px; color: #666">{{ formatFileSize(file.fileSize) }}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td class="text-center py-2">
                                      <v-chip size="x-small" color="primary" variant="outlined">
                                        {{ file.cifList ? file.cifList.length : 0 }}
                                      </v-chip>
                                    </td>
                                    <td class="text-center py-2" style="font-size: 11px">
                                      {{ formatUploadDate(file.uploadDate) }}
                                    </td>
                                    <td class="text-center py-2">
                                      <v-btn variant="outlined" color="primary" size="x-small" class="mr-1" @click="downloadQuestionCifFile(file)">
                                        <v-icon size="14">mdi-download</v-icon>
                                      </v-btn>
                                      <v-btn variant="outlined" color="error" size="x-small" @click="removeQuestionCifFile(index, fileIndex)">
                                        <v-icon size="14">mdi-delete</v-icon>
                                      </v-btn>
                                    </td>
                                  </tr>
                                </tbody>
                              </v-table>
                              <div v-if="question.cifFiles.length > 1" class="d-flex justify-end mt-2">
                                <v-btn variant="outlined" color="error" size="x-small" @click="clearAllQuestionCifFiles(index)">
                                  <v-icon size="14" class="mr-1">mdi-delete-sweep</v-icon>
                                  Clear All
                                </v-btn>
                              </div>
                            </v-card>
                          </div>

                          <!-- Segment and Wealth Membership (Only show when targetGroup is 'persona') -->
                          <v-row v-if="question.targetGroup === 'persona'" class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Segment</label>
                              <v-select
                                v-model="question.segment"
                                :items="segmentOptions"
                                item-title="name"
                                item-value="valueString"
                                variant="solo"
                                density="compact"
                                multiple
                                chips
                                class="custom-select"
                                hide-details
                                flat
                                :disabled="!canEdit"
                              ></v-select>
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Wealth Membership</label>
                              <v-select
                                v-model="question.wealthMembership"
                                :items="wealthMembershipOptions"
                                item-title="name"
                                item-value="valueString"
                                variant="solo"
                                density="compact"
                                multiple
                                chips
                                class="custom-select"
                                hide-details
                                flat
                                :disabled="!canEdit"
                              ></v-select>
                            </v-col>
                          </v-row>

                          <!-- Effective Dates -->
                          <v-row class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Effective Start Date</label>
                              <input v-model="question.effectiveStartDate" type="date" class="custom-input" :disabled="!canEdit" />
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Effective End Date</label>
                              <input
                                v-model="question.effectiveEndDate"
                                type="date"
                                class="custom-input"
                                :class="{ 'input-error': !validateDateRange(question.effectiveStartDate, question.effectiveEndDate) }"
                                :disabled="!canEdit"
                              />
                              <div v-if="!validateDateRange(question.effectiveStartDate, question.effectiveEndDate)" class="error-message">End Date must be on or after Start Date</div>
                            </v-col>
                          </v-row>

                          <!-- Enable -->
                          <div class="d-flex align-center justify-end">
                            <v-switch inset class="ma-0" v-model="question.questionEnabled" label="Enable" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="5" class="split-preview-col">
                  <div class="mobile-preview-sticky">
                    <div style="background-color: #f3f3f3; border-radius: 14px; padding: 24px 0" class="d-flex justify-center align-center">
                      <v-skeleton-loader v-if="loading" type="image" width="268" height="582" class="mobile-skeleton"></v-skeleton-loader>
                      <MobileTaxPlannerPreview
                        v-else
                        :features="suggestionConfig.features"
                        :questions="suggestionQuestionConfig.questions"
                        :config="config"
                        :enabled-languages="enabledLanguages"
                        :post-message-text="postMessageText"
                        :post-message-color="postMessagePreviewColor"
                        :suggest-bar-text="suggestBarText || suggestBarTextEN"
                        :genie-icon-url="genieChatIconUrl"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-window-item>

            <!-- Genie AI Character Tab -->
            <v-window-item>
              <v-row style="padding: 25px" class="preview-split-row">
                <v-col cols="12" md="7" class="split-form-col">
                  <!-- Loading Skeleton for Genie AI Characters -->
                  <div v-if="loading">
                    <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                    <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                  </div>

                  <!-- Genie AI Character Content -->
                  <div v-else class="mb-4">
                    <div class="d-flex start align-center mb-3">
                      <h3 class="language-config-title mr-3">Genie AI Character</h3>
                    </div>

                    <div class="suggestion-form">
                      <v-row>
                        <v-col cols="6">
                          <div class="mb-3">
                            <v-btn color="#4E3982" variant="outlined" style="height: 40px; border-radius: 12px; text-transform: none" @click="addNewGenieCharacter" :disabled="!canAdd">
                              <v-icon start size="small">mdi-plus</v-icon>
                              Add Character
                            </v-btn>
                            <span class="ml-2 text-caption">{{ genieCharacters.length }} Character(s)</span>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Dynamic Characters -->
                      <div v-for="({ character, index }) in paginatedGenieCharacters" :key="index" class="feature-section mb-4 pa-4" style="border: 1px solid #e0e0e0; border-radius: 8px">
                        <div class="d-flex align-center mb-2">
                          <h4>Character {{ index + 1 }}</h4>
                          <div style="flex-grow: 1; background-color: #000000; opacity: 20%; height: 1px" class="mx-2"></div>

                          <!-- Preview Button -->
                          <v-btn icon size="small" variant="plain" color="#4E3982" @click="previewGenieCharacter(index)" class="mr-1" :title="'Preview this character'">
                            <v-icon>mdi-eye</v-icon>
                          </v-btn>

                          <!-- Remove Character Button -->
                          <v-btn v-if="genieCharacters.length > 1" icon size="small" variant="plain" color="error" @click="removeGenieCharacter(index)" class="mr-2">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>

                          <!-- Collapse/Expand Button -->
                          <v-btn icon size="small" variant="plain" @click="character.collapsed = !character.collapsed">
                            <v-icon size="28">{{ character.collapsed ? 'mdi-menu-down' : 'mdi-menu-up' }}</v-icon>
                          </v-btn>
                        </div>

                        <div v-if="!character.collapsed">
                          <!-- Character Name -->
                          <div class="mb-3">
                            <label class="form-label">Character Name <span style="color: red">*</span></label>
                            <input v-model="character.characterName" placeholder="Enter character name" class="custom-input" :disabled="!canEdit" />
                          </div>

                          <!-- Icon Upload - Chat icon only -->
                          <div class="mb-3">
                            <label class="form-label">Character Icons <span style="color: red">*</span></label>
                            <div class="text-caption text-grey mb-1">Any image type, max 10MB per file. GIF format is validated before upload.</div>
                            <v-row>
                              <!-- Chat Icon -->
                              <v-col cols="3">
                                <div class="text-center mb-1" style="font-size: 12px; font-weight: 500; color: #666">Chat</div>
                                <div class="genie-icon-upload-box" @click="canEdit ? triggerGenieIconUpload(index, 'chat') : null" :style="!canEdit ? 'opacity: 0.6; cursor: not-allowed; pointer-events: none' : ''">
                                  <v-img v-if="character.iconChatUrl" :src="character.iconChatUrl" width="100%" height="100%" cover style="border-radius: 8px">
                                    <template v-slot:error>
                                      <div class="d-flex align-center justify-center fill-height">
                                        <v-icon color="grey" size="28">mdi-image-broken-variant</v-icon>
                                      </div>
                                    </template>
                                  </v-img>
                                  <div v-else class="genie-icon-placeholder">
                                    <v-icon color="#ccc" size="28">mdi-image-plus</v-icon>
                                    <span style="font-size: 10px; color: #999; margin-top: 4px">Upload</span>
                                  </div>
                                </div>
                              </v-col>
                            </v-row>
                          </div>

                          <div v-if="character.iconValidationTouched && getMissingGenieIconLabels(character).length" class="error-message" role="alert">
                            Character Icons are required: {{ getMissingGenieIconLabels(character).join(', ') }}
                          </div>

                          <!-- Effective Dates -->
                          <v-row class="mb-3">
                            <v-col cols="6">
                              <label class="form-label">Effective Start Date <span style="color: red">*</span></label>
                              <input v-model="character.effectiveStartDate" type="date" class="custom-input" :class="{ 'input-error': !character.effectiveStartDate }" :disabled="!canEdit" />
                              <div v-if="!character.effectiveStartDate" class="error-message">Effective Start Date is required</div>
                            </v-col>
                            <v-col cols="6">
                              <label class="form-label">Effective End Date</label>
                              <input
                                v-model="character.effectiveEndDate"
                                type="date"
                                class="custom-input"
                                :class="{ 'input-error': !validateDateRange(character.effectiveStartDate, character.effectiveEndDate) }"
                                :disabled="!canEdit"
                              />
                              <div v-if="!validateDateRange(character.effectiveStartDate, character.effectiveEndDate)" class="error-message">End Date must be on or after Start Date</div>
                            </v-col>
                          </v-row>

                          <!-- Active Switch -->
                          <div class="d-flex align-center justify-end">
                            <v-switch inset class="ma-0" v-model="character.isActive" label="Active" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex align-center justify-end mt-2" style="gap: 12px">
                        <div class="text-caption text-medium-emphasis">Items per page:</div>
                        <div style="max-width: 90px">
                          <v-select
                            v-model="genieCharactersPerPage"
                            :items="genieCharactersPerPageOptions"
                            density="compact"
                            variant="outlined"
                            hide-details
                          ></v-select>
                        </div>
                        <v-pagination
                          v-model="genieCharactersPage"
                          :length="genieCharactersTotalPages"
                          :total-visible="4"
                          density="compact"
                        ></v-pagination>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Preview Column -->
                <v-col cols="12" md="5" class="split-preview-col">
                  <div class="mobile-preview-sticky">
                    <!-- Manual preview notice -->

                    <div style="background-color: #f3f3f3; border-radius: 14px; padding: 24px 0" class="d-flex justify-center align-center">
                      <v-skeleton-loader v-if="loading" type="image" width="268" height="582" class="mobile-skeleton"></v-skeleton-loader>
                      <MobileTaxPlannerPreview
                        v-else
                        :features="suggestionConfig.features"
                        :questions="suggestionQuestionConfig.questions"
                        :config="config"
                        :enabled-languages="enabledLanguages"
                        :post-message-text="postMessageText"
                        :post-message-color="postMessagePreviewColor"
                        :suggest-bar-text="suggestBarText || suggestBarTextEN"
                        :genie-icon-url="genieChatIconUrl"
                      />
                    </div>
                    <div v-if="geniePreviewIsManual" class="d-flex align-center justify-space-between mt-2 px-2 py-2" style="background-color: #ede9f7; border-radius: 10px; border: 1px solid #c9bbec">
                      <span style="font-size: 12px; color: #4E3982">
                        <v-icon size="14" color="#4E3982" class="mr-1">mdi-eye</v-icon>
                        Preview: <strong>{{ genieCharacters[geniePreviewIndex]?.characterName || `Character ${geniePreviewIndex + 1}` }}</strong>
                      </span>
                      <v-btn size="x-small" variant="outlined" color="#4E3982" style="border-radius: 8px; text-transform: none; font-size: 11px" @click="resetGeniePreview">
                        <v-icon size="12" class="mr-1">mdi-restore</v-icon>
                        {{ $t('reset') }}
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-window-item>

            <!-- Tax Prefix Tab -->
            <v-window-item>
              <v-row style="padding: 25px">
                <v-col cols="12">
                  <TaxPrefixTab
                    ref="taxPrefixTab"
                    :mobile-configuration-id="configurationId"
                    :can-add="canAdd"
                    :can-edit="canEdit"
                    :can-delete="canDelete"
                  />
                </v-col>
              </v-row>
            </v-window-item>

            <!-- Tax Dialog Tab -->
            <v-window-item>
              <div style="padding: 25px">
                <TaxDialogTab ref="taxDialogTab" :mobile-configuration-id="configurationId" :can-edit="canEdit" />
              </div>
            </v-window-item>

          </v-window>

          <!-- Save Button for All Configurations -->
          <div v-if="permissions.edit" class="mb-4 d-flex justify-end px-8" style="gap: 1rem">
            <v-btn style="height: 45px; width: 130px; border-radius: 12px" color="#4E3982" @click="saveConfiguration" :loading="loading" :disabled="loading">
              <v-icon start>mdi-content-save</v-icon>
              {{ $t('save') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-container>

    <!-- Hidden File Input -->
    <input ref="imageInput" type="file" accept="image/*" style="display: none" aria-label="Feature image upload" @change="handleImageUpload" />

    <!-- Hidden Genie Icon File Input -->
    <input ref="genieIconInput" type="file" style="display: none" aria-label="Genie icon upload" @change="handleGenieIconUpload" />

    <!-- Hidden CIF File Inputs -->
    <input ref="featureCifInput" type="file" accept=".csv,.xlsx,.xls" style="display: none" @change="handleFeatureCifUpload" />
    <input ref="questionCifInput" type="file" accept=".csv,.xlsx,.xls" style="display: none" @change="handleQuestionCifUpload" />

    <!-- Image Crop Dialog -->
    <v-dialog v-model="imageCropDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Crop Image</span>
        </v-card-title>
        <v-card-text>
          <div class="crop-container" style="max-width: 100%; max-height: 400px; overflow: hidden">
            <Cropper
              ref="cropper"
              :src="selectedImageFile"
              :stencil-props="{
                aspectRatio: 1,
                resizable: true,
                movable: true,
              }"
              :canvas="{
                maxWidth: 400,
                maxHeight: 400,
              }"
              class="cropper"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="closeCropDialog"> Cancel </v-btn>
          <v-btn color="#4E3982" variant="flat" @click="cropImage"> Crop & Upload </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manage Suggestion Actions (Tax Planning scope: isTaxPlanningMode = true) -->
    <SuggestionActionManagerDialog
      v-model="showSuggestionActionManager"
      :is-tax-planning-mode="true"
      :can-edit="canEdit"
      @saved="GetSuggestionActions"
    />
  </div>
</template>

<script>
import { feature } from 'caniuse-lite';
import moment from 'moment';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import MobileTaxPlannerPreview from '@/components/MobileTaxPlannerPreview.vue';
import SuggestionActionManagerDialog from '@/components/SuggestionActionManagerDialog.vue';
import TaxPrefixTab from '@/components/TaxPlanningMobileConfiguration/TaxPrefixTab.vue';
import TaxDialogTab from '@/components/TaxPlanningMobileConfiguration/TaxDialogTab.vue';
import CifListUploader from '@/components/MobileConfiguration/CifListUploader.vue';
import PostMessageColorField, { isValidPostMessageColor, resolvePostMessageColor } from '@/components/PostMessageColorField.vue';
import { validateGenieIconFile } from '@/utils/genieIconValidation';

export default {
  name: 'TaxPlanningMobileConfiguration',
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  components: {
    Cropper,
    MobileTaxPlannerPreview,
    SuggestionActionManagerDialog,
    TaxPrefixTab,
    TaxDialogTab,
    CifListUploader,
    PostMessageColorField,
  },
  data() {
    return {
      loading: false,
      flagNav: true,
      today: null,
      featuresLoading: false,
      questionsLoading: false,
      activeTab: 0,
      configurationId: null, // Store the configuration ID for updates
      postMessageText: 'AI อาจตอบคลาดเคลื่อนได้ หากพบข้อผิดพลาด กรุณาแจ้งให้เราทราบ',
      postMessageTextEN: '',
      postMessageTextCN: '',
      // Empty = mobile app uses its own default post message colour.
      postMessageColor: '',
      postMessageDefaultColor: '#D6DEDD',
      suggestBarText: '',
      suggestBarTextEN: '',
      suggestBarTextCN: '',
      taxHistoryTitle: '',
      taxHistoryTitleEN: '',
      taxHistoryTitleCN: '',
      // Options from API (similar to ProactiveMessage)
      segmentOptions: [],
      wealthMembershipOptions: [],
      actionOptions: [],
      showSuggestionActionManager: false,
      ADD_ACTION_VALUE: '__ADD_ACTION__',
      targetGroupOptions: ['all', 'persona', 'cif-list'],
      cacheScopeOptions: [
        { label: 'Shared (All Users)', value: 'shared' },
        { label: 'User Level (Per User)', value: 'user' }
      ],

      // Model selection
      selectedModel: null,
      aiModelOptions: [],
      uniqueAiModelOptions: [],

      // Mobile Information
      appTitle: '',
      mobileInformationHeader: '',
      mobileInformation: '',
      mobileInformationEN: '',

      // Timeout Configuration
      timeoutDuration: null,
      timeoutMessage: '',
      timeoutMessageEN: '',
      timeoutMessageCN: '',

      // Tax Planning Mode (stored on the Tax Planning record; drives the ChatBotLHB / Tax/VisitLogs gate)
      taxPlanning: {
        mode: 'maintenance', // maintenance | enable | tryout
        whitelistCifFiles: [],
        blacklistCifFiles: [],
        availableCifFiles: [],
      },

      // Voice settings bundles from LH Bank Configuration › Voice Configs
      voiceConfigOptions: [],

      config: {
        languages: {
          thai: true,
          english: true,
          chinese: false,
        },
        voice: {
          enabled: true,
          type: 'tts',
          systemPrompt: '',
          geminiSystemPrompt: '',
          speechToTextType: 'device',
          textToSpeechType: 'default',
          voiceConfigurationId: null,
          endOfSpeechDetection: null,
          reformatForTTSPrompt: '',
          formatForTTSEnginePrompt: '',
          ttsModel: 'gpt-5-chat',
          ttsApiVersion: '2025-01-01-preview',
        },
        copyMessage: {
          enabled: true,
        },
        logging: {
          enabled: false,
        },
        postMessage: {
          enabled: false,
        },
        // Cache Configuration
        enableCache: false,
        cacheTTLMinutes: 1440,
        cacheScope: 'shared',
        enableSemanticCache: false,
        similarityThreshold: 0.95,
      },
      suggestionConfig: {
        enabled: true,
        features: [], // Dynamic array of features from API
      },

      // Dialog states for image upload and cropping
      imageUploadDialog: false,
      imageCropDialog: false,
      selectedFeatureIndex: null,
      selectedImageFile: null,
      croppedImageBlob: null,
      suggestionQuestionConfig: {
        enabled: false,
        questions: [], // Dynamic array of questions from API
      },

      // Track deleted items to remove on save
      featuresToDelete: [], // Array of feature numbers to delete
      questionsToDelete: [], // Array of question numbers to delete
      genieCharactersToDelete: [], // Array of genie character IDs to delete

      // CIF File Upload Management
      currentFeatureIndexForCif: null, // Track which feature is uploading CIF
      currentQuestionIndexForCif: null, // Track which question is uploading CIF

      // Genie AI Character
      genieCharacters: [],
      genieCharactersPage: 1,
      genieCharactersPerPage: 5,
      genieCharactersPerPageOptions: [5, 10, 20],
      geniePreviewIndex: null, // Which character is being manually previewed
      geniePreviewIsManual: false, // true = user clicked preview; false = auto from conditions
      genieIconUploadTarget: null, // { index, field } for current icon upload
    };
  },
  computed: {
    postMessagePreviewColor() {
      return resolvePostMessageColor(this.postMessageColor, this.postMessageDefaultColor);
    },
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('TaxPlanningMobileConfigurations');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('TaxPlanningMobileConfigurations'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('TaxPlanningMobileConfigurations'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('TaxPlanningMobileConfigurations'));
    },
    actionSelectItems() {
      if (!this.canEdit) return this.actionOptions;
      return [...this.actionOptions, { name: this.ADD_ACTION_VALUE, __isAddAction: true }];
    },
    // Display label of the voice settings bundle selected for this mode
    selectedVoiceConfigName() {
      const selectedId = this.config.voice.voiceConfigurationId;
      if (selectedId === null || selectedId === undefined) return '—';
      const matched = (this.voiceConfigOptions || []).find((option) => option.id === selectedId);
      return matched ? this.voiceConfigItemTitle(matched) : `#${selectedId}`;
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('TaxPlanningMobileConfigurations'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('TaxPlanningMobileConfigurations'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('TaxPlanningMobileConfigurations'),
      }
    },
    hasEnabledFeatures() {
      return this.suggestionConfig.features.some((feature) => feature && feature.featureEnabled);
    },
    hasEnabledQuestions() {
      return this.suggestionQuestionConfig.questions.some((question) => question && question.questionEnabled);
    },
    enabledLanguages() {
      const languages = [];
      if (this.config.languages.thai) {
        languages.push({ key: 'thai', text: 'ไทย' });
      }
      if (this.config.languages.english) {
        languages.push({ key: 'english', text: 'ENG' });
      }
      if (this.config.languages.chinese) {
        languages.push({ key: 'chinese', text: '中文' });
      }
      return languages;
    },
    endOfSpeechDetectionRules() {
      return [
        (v) => v === null || v === '' || v === undefined || Number.isInteger(Number(v)) || 'Must be a whole number',
        (v) => v === null || v === '' || v === undefined || Number(v) >= 0 || 'Must be a positive number',
      ];
    },
    // Method to get feature image with fallback
    getFeatureImageUrl() {
      return (feature, index) => {
        if (feature && feature.imageUrl) {
          return feature.imageUrl;
        }
        // Return null to show placeholder square box instead of default images
        return null;
      };
    },
    activeGenieCharacterIndex() {
      const today = new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const candidates = this.genieCharacters
        .map((c, idx) => ({ c, idx }))
        .filter(({ c }) => {
          if (!c.isActive) return false;
          const start = c.effectiveStartDate ? c.effectiveStartDate.slice(0, 10) : null;
          if (!start || start > today) return false;
          const end = c.effectiveEndDate ? c.effectiveEndDate.slice(0, 10) : null;
          return !end || end >= today;
        });
      if (candidates.length === 0) return null;
      candidates.sort((a, b) => (b.c.effectiveStartDate || '').localeCompare(a.c.effectiveStartDate || ''));
      return candidates[0].idx;
    },
    currentGeniePreviewIndex() {
      if (this.geniePreviewIsManual && this.geniePreviewIndex !== null) {
        return this.geniePreviewIndex;
      }
      return this.activeGenieCharacterIndex;
    },
    genieChatIconUrl() {
      const idx = this.currentGeniePreviewIndex;
      if (idx === null || idx === undefined) return null;
      const character = this.genieCharacters[idx];
      return character && character.iconChatUrl ? character.iconChatUrl : null;
    },
    genieCharactersTotalPages() {
      return Math.max(1, Math.ceil(this.genieCharacters.length / this.genieCharactersPerPage));
    },
    paginatedGenieCharacters() {
      const start = (this.genieCharactersPage - 1) * this.genieCharactersPerPage;
      const end = start + this.genieCharactersPerPage;
      return this.genieCharacters.slice(start, end).map((character, localIndex) => ({
        character,
        index: start + localIndex,
      }));
    },
    hasInvalidGenieCharacterName() {
      return this.genieCharacters.some((character) => !character.characterName?.trim());
    },
    isSaveDisabled() {
      return this.loading || this.hasInvalidGenieCharacterName;
    },
  },
  watch: {
    genieCharactersPerPage() {
      this.genieCharactersPage = 1;
    },
    genieCharacters() {
      if (this.genieCharactersPage > this.genieCharactersTotalPages) {
        this.genieCharactersPage = this.genieCharactersTotalPages;
      }
    },
  },
  methods: {
    // Allow only positive numbers (0-9) for input
    allowOnlyPositiveNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      // Allow only digits (0-9)
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },

    // Allow only decimal numbers (0-9 and decimal point) for input
    allowOnlyDecimalNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      // Allow digits (0-9) and decimal point (.)
      if ((charCode < 48 || charCode > 57) && charCode !== 46) {
        event.preventDefault();
      }
      // Prevent multiple decimal points
      if (charCode === 46 && event.target.value.includes('.')) {
        event.preventDefault();
      }
    },

    // Validate End Date >= Start Date for a single item
    validateDateRange(startDate, endDate) {
      if (!startDate || !endDate) return true;
      return endDate >= startDate;
    },

    // Validate all suggestion features dates
    validateSuggestionFeatures() {
      const errors = [];
      this.suggestionConfig.features.forEach((feature, index) => {
        if (!this.validateDateRange(feature.effectiveStartDate, feature.effectiveEndDate)) {
          errors.push(`Feature ${index + 1}: End Date must be on or after Start Date`);
        }
      });
      return errors;
    },

    // Validate all suggestion questions dates
    validateSuggestionQuestions() {
      const errors = [];
      this.suggestionQuestionConfig.questions.forEach((question, index) => {
        if (!this.validateDateRange(question.effectiveStartDate, question.effectiveEndDate)) {
          errors.push(`Question ${index + 1}: End Date must be on or after Start Date`);
        }
      });
      return errors;
    },

    async saveConfiguration() {
      this.loading = true;
      try {
        // Validate date ranges before saving
        const featureErrors = this.suggestionConfig.enabled ? this.validateSuggestionFeatures() : [];
        const questionErrors = this.suggestionQuestionConfig.enabled ? this.validateSuggestionQuestions() : [];
        const genieErrors = this.validateGenieCharacters();
        const taxPrefixErrors = this.$refs.taxPrefixTab?.validateTaxPrefixPatterns() || [];
        const postMessageColorErrors = isValidPostMessageColor(this.postMessageColor) ? [] : [this.$t('taxHistoryStyleColorInvalid')];
        const allErrors = [...featureErrors, ...questionErrors, ...genieErrors, ...taxPrefixErrors, ...postMessageColorErrors];

        if (allErrors.length > 0) {
          this.loading = false;
          this.$swal.fire({
            title: 'Validation Error',
            html: allErrors.join('<br>'),
            icon: 'error',
            confirmButtonText: 'OK',
          });
          return;
        }

        // Prepare the main configuration request
        const mobileConfigRequest = {
          languageThaiEnabled: this.config.languages.thai,
          languageEnglishEnabled: this.config.languages.english,
          languageChineseEnabled: this.config.languages.chinese,
          voiceEnabled: this.config.voice.enabled,
          voiceType: this.config.voice.type,
          copyMessageEnabled: this.config.copyMessage.enabled,
          suggestionFeatureEnabled: this.suggestionConfig.enabled,
          suggestionQuestionEnabled: this.suggestionQuestionConfig.enabled,
          loggingEnabled: this.config.logging.enabled,
          postMessageEnabled: this.config.postMessage.enabled,
          postMessage: this.postMessageText,
          postMessageEN: this.postMessageTextEN,
          postMessageCN: this.postMessageTextCN,
          postMessageColor: (this.postMessageColor || '').trim() || null,
          suggestBarText: this.suggestBarText,
          suggestBarTextEN: this.suggestBarTextEN,
          suggestBarTextCN: this.suggestBarTextCN,
          taxHistoryTitle: this.taxHistoryTitle,
          taxHistoryTitleEN: this.taxHistoryTitleEN,
          taxHistoryTitleCN: this.taxHistoryTitleCN,
          voiceSystemPrompt: this.config.voice.systemPrompt,
          geminiVoiceSystemPrompt: this.config.voice.geminiSystemPrompt,
          speechToTextType: this.config.voice.speechToTextType || 'device',
          textToSpeechType: this.config.voice.textToSpeechType || 'default',
          voiceConfigurationId: this.config.voice.voiceConfigurationId ?? null,
          endOfSpeechDetection: this.config.voice.endOfSpeechDetection || null,
          reformatForTTSPrompt: this.config.voice.reformatForTTSPrompt || '',
          formatForTTSEnginePrompt: this.config.voice.formatForTTSEnginePrompt || '',
          ttsModel: this.config.voice.ttsModel || 'gpt-5-chat',
          ttsApiVersion: this.config.voice.ttsApiVersion || '2025-01-01-preview',
          humanMode: this.config.humanMode || false,
          ttsEnabled: this.config.ttsEnabled || false,
          selectedModel: this.selectedModel || null,
          appTitle: this.appTitle || '',
          mobileInformationHeader: this.mobileInformationHeader || '',
          mobileInformation: this.mobileInformation,
          mobileInformationEN: this.mobileInformationEN,
          timeoutDuration: this.timeoutDuration || null,
          timeoutMessage: this.timeoutMessage || '',
          timeoutMessageEN: this.timeoutMessageEN || '',
          timeoutMessageCN: this.timeoutMessageCN || '',
          // Cache Configuration
          enableCache: this.config.enableCache || false,
          cacheTTLMinutes: this.config.cacheTTLMinutes || 1440,
          cacheScope: this.config.cacheScope || 'shared',
          enableSemanticCache: this.config.enableSemanticCache || false,
          similarityThreshold: this.config.similarityThreshold || 0.95,
          // Tax Planning Mode
          taxPlanningMode: this.taxPlanning.mode || 'maintenance',
          taxPlanningWhitelistCifFiles: this.taxPlanning.whitelistCifFiles || [],
          taxPlanningBlacklistCifFiles: this.taxPlanning.blacklistCifFiles || [],
          taxPlanningAvailableCifFiles: this.taxPlanning.availableCifFiles || [],
        };

        // Use Update if we have an ID (including 0), Create if we don't
        const hasId = this.configurationId !== null && this.configurationId !== undefined;
        const apiMethod = hasId ? 'UpdateTaxPlanningMobileConfiguration' : 'CreateTaxPlanningMobileConfiguration';
        if (hasId) {
          mobileConfigRequest.ID = this.configurationId;
        }

        // Verify the API method exists
        if (!this.$call[apiMethod]) {
          throw new Error(`API method ${apiMethod} not found in $call wrapper`);
        }

        // STEP 1: Save main configuration
        let mainConfigResponse = await new Promise((resolve, reject) => {
          this.$call[apiMethod](
            mobileConfigRequest,
            (response) => {
              if (response && (response.Status === 'Successful' || response.status === 0)) {
                // Store the ID if it was a create operation
                if (!this.configurationId && response.data && response.data.id) {
                  this.configurationId = response.data.id;
                }
                resolve(response);
              } else {
                reject(new Error(response?.Message || 'Save main configuration failed'));
              }
            },
            (error) => {
              console.error('Save main configuration error:', error);
              reject(error);
            }
          );
        });

        // STEP 2: Delete marked features
        if (this.featuresToDelete.length > 0) {
          this.featuresToDelete.forEach((e) => {
            const removeIdx = this.suggestionConfig.features.findIndex((f) => f && f.featureId === e);
            if (removeIdx !== -1) {
              this.suggestionConfig.features.splice(removeIdx, 1);
            }
          });
          this.deleteMarkedFeatures();
        }

        // STEP 3: Delete marked questions
        if (this.questionsToDelete.length > 0) {
          this.questionsToDelete.forEach((e) => {
            const removeIdx = this.suggestionQuestionConfig.questions.findIndex((q) => q && q.questionId === e);
            if (removeIdx !== -1) {
              this.suggestionQuestionConfig.questions.splice(removeIdx, 1);
            }
          });
          this.deleteMarkedQuestions();
        }

        // Get the configuration ID for saving features and questions
        const configId = this.configurationId;
        // STEP 4: Save suggestion features if enabled
        if (this.suggestionConfig.enabled && configId) {
          await this.saveSuggestionFeatures(configId);
        }

        // STEP 5: Save suggestion questions if enabled
        if (this.suggestionQuestionConfig.enabled && configId) {
          await this.saveSuggestionQuestions(configId);
        }

        // STEP 6: Delete marked genie characters
        if (this.genieCharactersToDelete.length > 0) {
          await this.deleteMarkedGenieCharacters();
        }

        // STEP 7: Save genie AI characters
        if (this.genieCharacters.length > 0 && configId) {
          await this.saveGenieCharacters(configId);
        }

        // STEP 8: Save tax prefix patterns (handles its own queued deletes first)
        if (configId && this.$refs.taxPrefixTab) {
          await this.$refs.taxPrefixTab.saveTaxPrefixPatterns(configId);
        }

        // STEP 9: Save tax dialogs (the tab has no save control of its own)
        if (configId && this.$refs.taxDialogTab) {
          await this.$refs.taxDialogTab.saveTaxDialogs(configId);
        }

        this.$swal.fire({
          title: this.$t('success'),
          text: this.$t('configurationSaved'),
          icon: 'success',
          confirmButtonText: this.$t('ok'),
        });
        this.loadConfiguration(); // Reload to get updated data
      } catch (error) {
        console.error('Save configuration error:', error);

        if (error.response?.status === 401) {
          this.$swal
            .fire({
              title: this.$t('error'),
              text: 'Authentication required. Please login again.',
              icon: 'error',
              confirmButtonText: this.$t('ok'),
            })
            .then(() => {
              // Redirect to home or refresh page
              window.location.href = '/';
            });
          return;
        }

        this.$swal.fire({
          title: this.$t('error'),
          text: error.response?.data?.Message || error.message || this.$t('saveFailed'),
          icon: 'error',
          confirmButtonText: this.$t('ok'),
        });
      } finally {
        this.loading = false;
      }
    },

    async deleteMarkedFeatures() {
      const deletePromises = this.featuresToDelete.map((featureId) => {
        return new Promise((resolve, reject) => {
          this.$call.DeleteTaxPlanningSuggestionFeature(
            featureId,
            (response) => {
              if (response && (response.Status === 'Successful' || response.status === 0)) {
                resolve(response);
              } else {
                resolve(response);
              }
            },
            (error) => {
              console.error(`Error deleting feature ${featureId}:`, error);
              resolve(); // Continue even if delete fails
            }
          );
        });
      });

      try {
        await Promise.all(deletePromises);
        // Clear the delete list
        this.featuresToDelete = [];
      } catch (error) {
        console.error('Error deleting features:', error);
        // Clear anyway to prevent re-deletion
        this.featuresToDelete = [];
      }
    },

    async deleteMarkedQuestions() {
      const deletePromises = this.questionsToDelete.map((questionId) => {
        return new Promise((resolve, reject) => {
          this.$call.DeleteTaxPlanningSuggestionQuestion(
            questionId,
            (response) => {
              if (response && (response.Status === 'Successful' || response.status === 0)) {
                resolve(response);
              } else {
                resolve(response);
              }
            },
            (error) => {
              console.error(`Error deleting question ${questionId}:`, error);
              resolve(); // Continue even if delete fails
            }
          );
        });
      });

      try {
        await Promise.all(deletePromises);
        // Clear the delete list
        this.questionsToDelete = [];
      } catch (error) {
        console.error('Error deleting questions:', error);
        // Clear anyway to prevent re-deletion
        this.questionsToDelete = [];
      }
    },

    async saveSuggestionFeatures(configId) {
      // Collect only enabled features for parallel processing
      const featuresToSave = [];
      this.suggestionConfig.features.forEach((feature, index) => {
        if (feature) {
          feature.segment = (Array.isArray(feature.segment) ? feature.segment : []).filter((segment) => this.segmentOptions.some((option) => option.valueString === segment));
          feature.wealthMembership = (Array.isArray(feature.wealthMembership) ? feature.wealthMembership : []).filter((membership) => this.wealthMembershipOptions.some((option) => option.valueString === membership));
          const featureData = {
            ID: feature.featureId || null,
            FeatureNumber: index + 1,
            MessageThai: feature.messages.thai || '',
            MessageEnglish: feature.messages.english || '',
            MessageChinese: feature.messages.chinese || '',
            ModelName: feature.modelName || '',
            TargetGroup: feature.targetGroup || 'all',
            Priority: feature.priority ? parseInt(feature.priority) : null,
            Segment: feature.segment ? feature.segment.toString() : null,
            WealthMembership: feature.wealthMembership ? feature.wealthMembership.toString() : null,
            EffectiveStartDate: this.formatDateForAPI(feature.effectiveStartDate),
            EffectiveEndDate: this.formatDateForAPI(feature.effectiveEndDate),
            FeatureEnabled: feature.featureEnabled,
            Action: feature.action || null,
            IconPath: feature.imageUrl || null,
            isNew: feature.isNew || false,
            cifFiles: feature.cifFiles || [],
          };

          featuresToSave.push({ featureNumber: index + 1, data: featureData });
        }
      });

      if (featuresToSave.length === 0) {
        console.log('No enabled features to save');
        return;
      }
      // Save all features in parallel using Promise.all
      const savePromises = featuresToSave.map(({ featureNumber, data }) => this.saveOrUpdateSingleFeature(configId, featureNumber, data));

      try {
        await Promise.all(savePromises);
      } catch (error) {
        console.error('Error saving suggestion features:', error);
        throw error;
      }
    },

    async saveOrUpdateSingleFeature(configId, featureNumber, featureData) {
      // Try update first, then create if it fails
      try {
        if (featureData.isNew) {
          await new Promise((resolve, reject) => {
            this.$call.CreateTaxPlanningSuggestionFeature(
              configId,
              featureData,
              (response) => {
                if (response && (response.Status === 'Successful' || response.status === 0)) {
                  resolve(response);
                } else {
                  reject(new Error(`Failed to create feature ${featureNumber}: ${response?.Message || 'Unknown error'}`));
                }
              },
              (error) => {
                console.error(`Error creating feature ${featureNumber}:`, error);
                reject(error);
              }
            );
          });
        } else {
          await new Promise((resolve, reject) => {
            this.$call.UpdateTaxPlanningSuggestionFeature(
              configId,
              featureData,
              (response) => {
                if (response && (response.Status === 'Successful' || response.status === 0)) {
                  resolve(response);
                } else {
                  reject(new Error('Update failed, will try create'));
                }
              },
              (error) => {
                console.log(`Update error for feature ${featureNumber}, trying create:`, error);
                reject(error);
              }
            );
          });
        }
      } catch (updateError) {
        // If update fails, try create
        console.log(`Attempting to create feature ${featureNumber} after update failed`);
      }
    },

    async saveSuggestionQuestions(configId) {
      // Collect only enabled questions for parallel processing
      const questionsToSave = [];
      this.suggestionQuestionConfig.questions.forEach((question, index) => {
        if (question) {
          question.segment = (Array.isArray(question.segment) ? question.segment : []).filter((segment) => this.segmentOptions.some((option) => option.valueString === segment));
          question.wealthMembership = (Array.isArray(question.wealthMembership) ? question.wealthMembership : []).filter((membership) => this.wealthMembershipOptions.some((option) => option.valueString === membership));
          const questionData = {
            ID: question.questionId || null,
            QuestionNumber: index + 1,
            MessageThai: question.messages.thai || '',
            MessageEnglish: question.messages.english || '',
            MessageChinese: question.messages.chinese || '',
            TargetGroup: question.targetGroup || 'all',
            Priority: question.priority ? parseInt(question.priority) : null,
            Segment: question.segment ? question.segment.toString() : null,
            WealthMembership: question.wealthMembership ? question.wealthMembership.toString() : null,
            EffectiveStartDate: this.formatDateForAPI(question.effectiveStartDate),
            EffectiveEndDate: this.formatDateForAPI(question.effectiveEndDate),
            QuestionEnabled: question.questionEnabled,
            isNew: question.isNew || false,
            cifFiles: question.cifFiles || [],
          };

          questionsToSave.push({ questionNumber: index + 1, data: questionData });
        }
      });

      if (questionsToSave.length === 0) {
        console.log('No enabled questions to save');
        return;
      }
      // Save all questions in parallel using Promise.all
      const savePromises = questionsToSave.map(({ questionNumber, data }) => this.saveOrUpdateSingleQuestion(configId, questionNumber, data));

      try {
        await Promise.all(savePromises);
      } catch (error) {
        console.error('Error saving suggestion questions:', error);
        throw error;
      }
    },

    async saveOrUpdateSingleQuestion(configId, questionNumber, questionData) {
      // Try update first, then create if it fails
      try {
        if (questionData.isNew) {
          await new Promise((resolve, reject) => {
            this.$call.CreateTaxPlanningSuggestionQuestion(
              configId,
              questionData,
              (response) => {
                if (response && (response.Status === 'Successful' || response.status === 0)) {
                  resolve(response);
                } else {
                  reject(new Error(`Failed to create question ${questionNumber}: ${response?.Message || 'Unknown error'}`));
                }
              },
              (error) => {
                console.error(`Error creating question ${questionNumber}:`, error);
                reject(error);
              }
            );
          });
        } else {
          await new Promise((resolve, reject) => {
            this.$call.UpdateTaxPlanningSuggestionQuestion(
              configId,
              questionData,
              (response) => {
                if (response && (response.Status === 'Successful' || response.status === 0)) {
                  resolve(response);
                } else {
                  reject(new Error('Update failed, will try create'));
                }
              },
              (error) => {
                console.log(`Update error for question ${questionNumber}, trying create:`, error);
                reject(error);
              }
            );
          });
        }
      } catch (updateError) {
        // If update fails, try create
        console.log(`Attempting to create question ${questionNumber} after update failed`);
      }
    },

    // Image upload methods
    openImageUpload(featureIndex) {
      this.selectedFeatureIndex = featureIndex;
      this.$refs.imageInput.click();
    },

    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file && file.type.startsWith('image/')) {
        // Create object URL for the cropper
        this.selectedImageFile = URL.createObjectURL(file);
        this.imageCropDialog = true;
      } else {
        this.$swal.fire({
          title: this.$t('error'),
          text: 'Please select a valid image file.',
          icon: 'error',
          confirmButtonText: this.$t('ok'),
        });
      }
    },

    cropImage() {
      const { canvas } = this.$refs.cropper.getResult();
      if (canvas) {
        // Create a new canvas with the desired output size (200x200)
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = 200;
        outputCanvas.height = 200;
        const ctx = outputCanvas.getContext('2d');

        // Draw the cropped image onto the output canvas
        ctx.drawImage(canvas, 0, 0, 200, 200);

        outputCanvas.toBlob(
          (blob) => {
            this.croppedImageBlob = blob;
            this.uploadCroppedImage();
          },
          'image/png',
          0.9
        );
      }
    },

    async uploadCroppedImage() {
      if (!this.croppedImageBlob || this.selectedFeatureIndex === null) return;

      try {
        // Create FormData with the cropped image blob
        const formData = new FormData();
        formData.append('entity', this.croppedImageBlob, `feature_${this.selectedFeatureIndex + 1}.png`);

        // Use existing GPTStream upload API
        const uploadPromise = new Promise((resolve, reject) => {
          this.$call.UploadTaxPlanningImage(
            {
              entity: formData,
              USER_CHANNEL: this.$root.User_Id || 'mobile_config',
            },
            (response) => {
              console.log('Image upload response:', response);
              if (response && response.status === 0 && response.data) {
                resolve(response.data);
              } else {
                reject(new Error(response?.message || 'Upload failed'));
              }
            },
            (error) => {
              console.error('Image upload error:', error);
              reject(error);
            }
          );
        });

        // Show loading state
        this.$swal.fire({
          title: 'Uploading...',
          text: 'Please wait while the image is being uploaded.',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          },
        });

        const imageUrl = await uploadPromise;

        // Update the feature with the new image URL
        if (this.suggestionConfig.features[this.selectedFeatureIndex]) {
          this.suggestionConfig.features[this.selectedFeatureIndex].imageUrl = imageUrl;
        }

        this.$swal.fire({
          title: this.$t('success'),
          text: 'Image uploaded successfully!',
          icon: 'success',
          confirmButtonText: this.$t('ok'),
          timer: 2000,
        });
      } catch (error) {
        console.error('Image upload error:', error);
        this.$swal.fire({
          title: this.$t('error'),
          text: error.message || 'Failed to upload image. Please try again.',
          icon: 'error',
          confirmButtonText: this.$t('ok'),
        });
      } finally {
        this.closeCropDialog();
      }
    },

    // Image error handling
    onImageError(feature, index) {
      console.warn(`Failed to load image for feature ${index + 1}:`, feature.imageUrl);
      // Don't clear imageUrl immediately, let v-img handle fallback to error slot
      // The computed property will provide a fallback image anyway
    },

    closeCropDialog() {
      this.imageCropDialog = false;
      if (this.selectedImageFile) {
        URL.revokeObjectURL(this.selectedImageFile); // Clean up object URL
      }
      this.selectedImageFile = null;
      this.croppedImageBlob = null;
      this.selectedFeatureIndex = null;
      // Clear the file input
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = '';
      }
    },

    // ============ Genie AI Character Methods ============

    addNewGenieCharacter() {
      this.genieCharacters.push({
        collapsed: false,
        characterName: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        isActive: true,
        iconProactiveUrl: '',
        iconChatFirstUrl: '',
        iconChatUrl: '',
        iconVoicePageUrl: '',
        iconValidationTouched: false,
        isNew: true,
        pendingFiles: {},
      });
      this.genieCharactersPage = this.genieCharactersTotalPages;
    },

    removeGenieCharacter(index) {
      if (this.genieCharacters.length <= 1) {
        this.$swal.fire({
          title: this.$t('error'),
          text: 'You must keep at least one character.',
          icon: 'warning',
          confirmButtonText: this.$t('ok'),
        });
        return;
      }

      const character = this.genieCharacters[index];
      if (character && !character.isNew && character.id) {
        if (!this.genieCharactersToDelete.includes(character.id)) {
          this.genieCharactersToDelete.push(character.id);
        }
      }

      // Reset manual preview if previewing the removed character
      if (this.geniePreviewIndex === index) {
        this.geniePreviewIndex = null;
        this.geniePreviewIsManual = false;
      } else if (this.geniePreviewIndex !== null && this.geniePreviewIndex > index) {
        this.geniePreviewIndex--;
      }

      this.genieCharacters.splice(index, 1);
    },

    previewGenieCharacter(index) {
      this.geniePreviewIndex = index;
      this.geniePreviewIsManual = true;
    },

    resetGeniePreview() {
      this.geniePreviewIsManual = false;
      this.geniePreviewIndex = null;
    },

    triggerGenieIconUpload(characterIndex, fieldName) {
      this.genieIconUploadTarget = { index: characterIndex, field: fieldName };
      const input = this.$refs.genieIconInput;
      input.accept = 'image/*';
      input.click();
    },

    async handleGenieIconUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const target = this.genieIconUploadTarget;
      if (!target) return;

      const validationError = await validateGenieIconFile(file);
      if (validationError) {
        this.$swal.fire({
          title: this.$t('error'),
          text: validationError,
          icon: 'error',
          confirmButtonText: this.$t('ok'),
        });
        this.genieIconUploadTarget = null;
        if (this.$refs.genieIconInput) this.$refs.genieIconInput.value = '';
        return;
      }

      const fieldMap = {
        chat: 'iconChatUrl',
      };

      const prop = fieldMap[target.field];
      const character = this.genieCharacters[target.index];
      if (prop && character) {
        // Revoke previous blob URL if any
        const existing = character.pendingFiles?.[target.field];
        if (existing?.blobUrl) URL.revokeObjectURL(existing.blobUrl);

        const blobUrl = URL.createObjectURL(file);
        if (!character.pendingFiles) character.pendingFiles = {};
        character.pendingFiles[target.field] = { file, blobUrl };
        character[prop] = blobUrl;
      }

      this.genieIconUploadTarget = null;
      if (this.$refs.genieIconInput) this.$refs.genieIconInput.value = '';
    },

    async uploadGenieCharacterPendingFiles(character) {
      const fieldMap = {
        chat: 'iconChatUrl',
      };

      if (!character.pendingFiles) return;

      const uploads = Object.entries(character.pendingFiles).map(([field, { file, blobUrl }]) => {
        const formData = new FormData();
        formData.append('entity', file, file.name);
        return new Promise((resolve, reject) => {
          this.$call.UploadTaxPlanningGenieCharacterIcon(
            { entity: formData, USER_CHANNEL: this.$root.User_Id || 'mobile_config', genieField: field },
            (response) => {
              if (response && response.status === 0 && response.data) {
                URL.revokeObjectURL(blobUrl);
                const prop = fieldMap[field];
                if (prop) character[prop] = response.data;
                resolve();
              } else {
                reject(new Error(response?.message || `Upload failed for ${field}`));
              }
            },
            (error) => reject(error)
          );
        });
      });

      await Promise.all(uploads);
      character.pendingFiles = {};
    },

    getMissingGenieIconLabels(character) {
      const iconFields = [
        { field: 'iconChatUrl', label: 'Chat' },
      ];

      return iconFields.filter(({ field }) => !String(character?.[field] || '').trim()).map(({ label }) => label);
    },

    validateGenieCharacters() {
      const errors = [];
      this.genieCharacters.forEach((character, index) => {
        const missingIconLabels = this.getMissingGenieIconLabels(character);
        if (missingIconLabels.length > 0) {
          character.iconValidationTouched = true;
          errors.push(`Character ${index + 1}: Character Icons are required (${missingIconLabels.join(', ')})`);
        }
        if (!character.characterName?.trim()) {
          errors.push(`Character ${index + 1}: Character Name is required`);
        }
        if (!character.effectiveStartDate) {
          errors.push(`Character ${index + 1}: Effective Start Date is required`);
        }
        if (!this.validateDateRange(character.effectiveStartDate, character.effectiveEndDate)) {
          errors.push(`Character ${index + 1}: End Date must be on or after Start Date`);
        }
      });
      return errors;
    },

    async saveGenieCharacters(configId) {
      // Upload any pending icon files before saving
      await Promise.all(this.genieCharacters.map((character) => this.uploadGenieCharacterPendingFiles(character)));

      const charactersToSave = this.genieCharacters.map((character, index) => ({
        ID: character.id || null,
        CharacterName: character.characterName || '',
        EffectiveStartDate: this.formatDateForAPI(character.effectiveStartDate),
        EffectiveEndDate: this.formatDateForAPI(character.effectiveEndDate),
        IsActive: character.isActive,
        IconProactiveUrl: character.iconProactiveUrl || null,
        IconChatFirstUrl: character.iconChatFirstUrl || null,
        IconChatUrl: character.iconChatUrl || null,
        IconVoicePageUrl: character.iconVoicePageUrl || null,
        isNew: character.isNew || false,
      }));

      const savePromises = charactersToSave.map((data) => {
        const apiMethod = data.isNew ? 'CreateTaxPlanningGenieAICharacter' : 'UpdateTaxPlanningGenieAICharacter';
        if (!this.$call[apiMethod]) {
          console.warn(`API method ${apiMethod} not found, skipping save for character`);
          return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
          this.$call[apiMethod](
            configId,
            data,
            (response) => {
              if (response && (response.Status === 'Successful' || response.status === 0)) {
                resolve(response);
              } else {
                reject(new Error(response?.Message || `Failed to save character`));
              }
            },
            (error) => {
              console.error('Error saving genie character:', error);
              resolve(); // Continue even if save fails
            }
          );
        });
      });

      await Promise.all(savePromises);
    },

    async deleteMarkedGenieCharacters() {
      if (!this.$call.DeleteTaxPlanningGenieAICharacter) return;

      const deletePromises = this.genieCharactersToDelete.map((characterId) => {
        return new Promise((resolve) => {
          this.$call.DeleteTaxPlanningGenieAICharacter(
            characterId,
            (response) => resolve(response),
            (error) => {
              console.error(`Error deleting genie character ${characterId}:`, error);
              resolve();
            }
          );
        });
      });

      await Promise.all(deletePromises);
      this.genieCharactersToDelete = [];
    },

    // Add new question
    addNewQuestion() {
      const newQuestion = {
        collapsed: false,
        messages: {
          thai: '',
          english: '',
          chinese: '',
        },
        targetGroup: 'all',
        priority: '',
        segment: [],
        wealthMembership: [],
        cifFiles: [], // Initialize CIF files array
        effectiveStartDate: '',
        effectiveEndDate: '',
        questionEnabled: false,
        isNew: true, // Mark as new (not saved to backend yet)
      };
      this.suggestionQuestionConfig.questions.push(newQuestion);
    },

    // Remove question
    removeQuestion(index) {
      if (this.suggestionQuestionConfig.questions.length <= 1) {
        this.$swal.fire({
          title: this.$t('error'),
          text: 'You must keep at least one question.',
          icon: 'warning',
          confirmButtonText: this.$t('ok'),
        });
        return;
      }

      const questionNumber = index + 1;
      const question = this.suggestionQuestionConfig.questions[index];

      // If this question exists in backend (has been saved before), mark it for deletion
      if (this.configurationId !== null && this.configurationId !== undefined && question && !question.isNew) {
        // Add to delete list if not already there
        if (!this.questionsToDelete.includes(question.questionId)) {
          this.questionsToDelete.push(question.questionId);
        }
      }

      // Remove from UI
      this.suggestionQuestionConfig.questions.splice(index, 1);
    },

    // Add new feature
    addNewFeature() {
      const newFeature = {
        collapsed: false,
        messages: {
          thai: '',
          english: '',
          chinese: '',
        },
        modelName: '',
        action: '',
        targetGroup: 'all',
        priority: '',
        segment: [],
        wealthMembership: [],
        cifFiles: [], // Initialize CIF files array
        effectiveStartDate: '',
        effectiveEndDate: '',
        featureEnabled: false,
        imageUrl: '', // For uploaded image
        isNew: true, // Mark as new (not saved to backend yet)
      };
      this.suggestionConfig.features.push(newFeature);
    },

    // Remove feature
    removeFeature(featureData, index) {
      if (this.suggestionConfig.features.length <= 1) {
        this.$swal.fire({
          title: this.$t('error'),
          text: 'You must keep at least one feature.',
          icon: 'warning',
          confirmButtonText: this.$t('ok'),
        });
        return;
      }
      if (this.configurationId !== null && this.configurationId !== undefined && featureData && !featureData.isNew) {
        // Add to delete list if not already there
        if (!this.featuresToDelete.includes(featureData.featureId)) {
          this.featuresToDelete.push(featureData.featureId);
        }
      }

      // Remove from UI
      this.suggestionConfig.features.splice(index, 1);
    },

    async loadConfiguration() {
      this.loading = true;
      this.featuresLoading = true;
      this.questionsLoading = true;

      try {
        // Use $call API instead of direct axios
        await new Promise((resolve, reject) => {
          this.$call.GetTaxPlanningMobileConfigurationByCompanyId(
            {
              companyId: 1,
              cifNo: '123456',
              language: 'TH',
              screen: 'HOME',
              segment: 'sa24',
              wealthMembership: 'WP',
              theme: 'dark',
            },
            (response) => {
              if (response && response.status === 0 && response.data) {
                console.log('Loaded mobile configuration:', response.data);
                const config = response.data;
                // Store the configuration ID for updates (handle both uppercase and lowercase)
                // Use !== undefined to handle ID = 0 case
                this.configurationId = config.ID !== undefined ? config.ID : config.id;

                // Map API response to component data
                this.config.languages.thai = config.languageThaiEnabled || false;
                this.config.languages.english = config.languageEnglishEnabled || false;
                this.config.languages.chinese = config.languageChineseEnabled || false;
                this.config.humanMode = config.humanMode || false;
                this.config.ttsEnabled = config.ttsEnabled || false;
                this.config.voice.enabled = config.voiceEnabled || false;
                this.config.voice.type = config.voiceType || 'tts';
                this.config.voice.systemPrompt = config.voiceSystemPrompt || '';
                this.config.voice.geminiSystemPrompt = config.geminiVoiceSystemPrompt || '';
                this.config.voice.speechToTextType = config.speechToTextType || 'device';
                this.config.voice.textToSpeechType = config.textToSpeechType || 'default';
                this.config.voice.voiceConfigurationId = config.voiceConfigurationId ?? null;
                this.config.voice.endOfSpeechDetection = config.endOfSpeechDetection || null;
                this.config.voice.reformatForTTSPrompt = config.reformatForTTSPrompt || '';
                this.config.voice.formatForTTSEnginePrompt = config.formatForTTSEnginePrompt || '';
                this.config.voice.ttsModel = config.ttsModel || 'gpt-5-chat';
                this.config.voice.ttsApiVersion = config.ttsApiVersion || '2025-01-01-preview';
                this.config.copyMessage.enabled = config.copyMessageEnabled || false;
                this.suggestionConfig.enabled = config.suggestionFeatureEnabled || false;
                this.suggestionQuestionConfig.enabled = config.suggestionQuestionEnabled || false;
                this.config.logging.enabled = config.loggingEnabled || false;
                this.config.postMessage.enabled = config.postMessageEnabled || false;
                this.postMessageText = config.postMessage;
                this.postMessageTextEN = config.postMessageEN;
                this.postMessageTextCN = config.postMessageCN;
                this.postMessageColor = config.postMessageColor || '';
                this.suggestBarText = config.suggestBarText || '';
                this.suggestBarTextEN = config.suggestBarTextEN || '';
                this.suggestBarTextCN = config.suggestBarTextCN || '';
                this.taxHistoryTitle = config.taxHistoryTitle || '';
                this.taxHistoryTitleEN = config.taxHistoryTitleEN || '';
                this.taxHistoryTitleCN = config.taxHistoryTitleCN || '';

                // Tax Planning Mode + CIF lists
                this.taxPlanning.mode = config.taxPlanningMode || 'maintenance';
                this.taxPlanning.whitelistCifFiles = config.taxPlanningWhitelistCifFiles || [];
                this.taxPlanning.blacklistCifFiles = config.taxPlanningBlacklistCifFiles || [];
                this.taxPlanning.availableCifFiles = config.taxPlanningAvailableCifFiles || [];

                // Load selected model (single value)
                this.selectedModel = config.selectedModel || null;

                // Load mobile information
                this.appTitle = config.appTitle || '';
                this.mobileInformationHeader = config.mobileInformationHeader || '';
                this.mobileInformation = config.mobileInformation || '';
                this.mobileInformationEN = config.mobileInformationEN || '';

                // Load timeout configuration
                this.timeoutDuration = config.timeoutDuration || null;
                this.timeoutMessage = config.timeoutMessage || '';
                this.timeoutMessageEN = config.timeoutMessageEN || '';
                this.timeoutMessageCN = config.timeoutMessageCN || '';

                // Load cache configuration
                this.config.enableCache = config.enableCache || false;
                this.config.cacheTTLMinutes = config.cacheTTLMinutes || 1440;
                this.config.cacheScope = config.cacheScope || 'shared';
                this.config.enableSemanticCache = config.enableSemanticCache || false;
                this.config.similarityThreshold = config.similarityThreshold || 0.95;

                // Load suggestion features if available
                if (config.suggestionFeatures && config.suggestionFeatures.length > 0) {
                  this.suggestionConfig.features = config.suggestionFeatures.map((feature, index) => ({
                    collapsed: false,
                    featureId: feature.id, // Store original feature number
                    messages: {
                      thai: feature.messageThai || '',
                      english: feature.messageEnglish || '',
                      chinese: feature.messageChinese || '',
                    },
                    modelName: feature.modelName || '',
                    action: feature.action || '',
                    targetGroup: feature.targetGroup || 'all',
                    priority: feature.priority || '',
                    segment: feature.segment ? feature.segment.split(',') : [],
                    wealthMembership: feature.wealthMembership ? feature.wealthMembership.split(',') : [],
                    effectiveStartDate: this.formatDateForInput(feature.effectiveStartDate) || '',
                    effectiveEndDate: this.formatDateForInput(feature.effectiveEndDate) || '',
                    featureEnabled: feature.featureEnabled || false,
                    imageUrl: feature.iconPath || '', // Load the image URL from API
                    cifFiles: feature.cifFiles
                      ? feature.cifFiles.map((cifFile) => ({
                          fileName: cifFile.fileName || '',
                          filePath: cifFile.filePath || '',
                          cifList: cifFile.cifList || [],
                          uploadDate: cifFile.uploadDate ? new Date(cifFile.uploadDate) : new Date(),
                          fileSize: cifFile.fileSize || 0,
                        }))
                      : [], // Load CIF files from API
                    isNew: false, // Mark as existing (from backend)
                  }));
                } else {
                  // Initialize with one empty feature if no features from API
                  this.suggestionConfig.features = [
                    {
                      collapsed: false,
                      messages: {
                        thai: '',
                        english: '',
                        chinese: '',
                      },
                      modelName: '',
                      action: '',
                      targetGroup: 'all',
                      priority: '',
                      segment: [],
                      wealthMembership: [],
                      effectiveStartDate: '',
                      effectiveEndDate: '',
                      featureEnabled: false,
                      imageUrl: '',
                      isNew: true, // Mark as new
                    },
                  ];
                }

                // Load suggestion questions if available
                if (config.suggestionQuestions && config.suggestionQuestions.length > 0) {
                  this.suggestionQuestionConfig.questions = config.suggestionQuestions.map((question, index) => ({
                    collapsed: false,
                    messages: {
                      thai: question.messageThai || '',
                      english: question.messageEnglish || '',
                      chinese: question.messageChinese || '',
                    },
                    targetGroup: question.targetGroup || 'all',
                    priority: question.priority || '',
                    segment: question.segment ? question.segment.split(',') : [],
                    wealthMembership: question.wealthMembership ? question.wealthMembership.split(',') : [],
                    effectiveStartDate: this.formatDateForInput(question.effectiveStartDate) || '',
                    effectiveEndDate: this.formatDateForInput(question.effectiveEndDate) || '',
                    questionEnabled: question.questionEnabled || false,
                    cifFiles: question.cifFiles
                      ? question.cifFiles.map((cifFile) => ({
                          fileName: cifFile.fileName || '',
                          filePath: cifFile.filePath || '',
                          cifList: cifFile.cifList || [],
                          uploadDate: cifFile.uploadDate ? new Date(cifFile.uploadDate) : new Date(),
                          fileSize: cifFile.fileSize || 0,
                        }))
                      : [], // Load CIF files from API
                    questionId: question.id, // Store original question ID
                    isNew: false, // Mark as existing (from backend)
                  }));
                } else {
                  // Initialize with one empty question if no questions from API
                  this.suggestionQuestionConfig.questions = [
                    {
                      collapsed: false,
                      messages: {
                        thai: '',
                        english: '',
                        chinese: '',
                      },
                      targetGroup: 'all',
                      priority: '',
                      segment: [],
                      wealthMembership: [],
                      effectiveStartDate: '',
                      effectiveEndDate: '',
                      questionEnabled: false,
                      isNew: true, // Mark as new
                    },
                  ];
                }

                // Load genie AI characters if available
                if (config.genieAICharacters && config.genieAICharacters.length > 0) {
                  this.genieCharacters = config.genieAICharacters.map((character) => ({
                    collapsed: false,
                    id: character.id,
                    characterName: character.characterName || '',
                    effectiveStartDate: this.formatDateForInput(character.effectiveStartDate) || '',
                    effectiveEndDate: this.formatDateForInput(character.effectiveEndDate) || '',
                    isActive: character.isActive !== undefined ? character.isActive : true,
                    iconProactiveUrl: character.iconProactiveUrl || '',
                    iconChatFirstUrl: character.iconChatFirstUrl || '',
                    iconChatUrl: character.iconChatUrl || '',
                    iconVoicePageUrl: character.iconVoicePageUrl || '',
                    iconValidationTouched: false,
                    isNew: false,
                    pendingFiles: {},
                  }));
                } else {
                  this.genieCharacters = [
                    {
                      collapsed: false,
                      characterName: '',
                      effectiveStartDate: '',
                      effectiveEndDate: '',
                      isActive: true,
                      iconProactiveUrl: '',
                      iconChatFirstUrl: '',
                      iconChatUrl: '',
                      iconVoicePageUrl: '',
                      iconValidationTouched: false,
                      isNew: true,
                      pendingFiles: {},
                    },
                  ];
                }

                resolve(response);
              } else if (response && response.Status === 'Error') {
                console.warn('No mobile configuration found for this company');
                // Keep default values
                resolve(response);
              } else {
                reject(new Error('Invalid response format'));
              }
            },
            (error) => {
              console.error('Failed to load configuration:', error);
              reject(error);
            }
          );
        });

        // Simulate realistic loading delays for better UX
        await new Promise((resolve) => setTimeout(resolve, 500)); // Features loading
        this.featuresLoading = false;

        await new Promise((resolve) => setTimeout(resolve, 300)); // Questions loading
        this.questionsLoading = false;
      } catch (error) {
        console.error('Failed to load configuration:', error);

        if (error.response?.status === 401) {
          console.error('401 Unauthorized - Authentication required');
          this.$swal
            .fire({
              title: this.$t('error'),
              text: 'Authentication required. Please login again.',
              icon: 'error',
              confirmButtonText: this.$t('ok'),
            })
            .then(() => {
              // Redirect to home or refresh page
              window.location.href = '/';
            });
          return;
        }

        // Set default values if loading fails
        this.config = {
          languages: { thai: true, english: true, chinese: false },
          voice: { enabled: true, type: 'tts' },
          copyMessage: { enabled: true },
        };

        this.featuresLoading = false;
        this.questionsLoading = false;
      } finally {
        this.loading = false;
      }
    },

    // Get options for dropdowns (similar to ProactiveMessage)
    async GetOptions() {
      return new Promise((resolve, reject) => {
        this.$call.GetProactiveMessageOptions(
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.segmentOptions = dataResponse.data.segmentOptions || [];
              this.wealthMembershipOptions = dataResponse.data.wealthMembershipOptions || [];
              resolve(dataResponse);
            } else {
              reject(new Error('Failed to load options'));
            }
          },
          (errorResource) => {
            console.error('Error getting options:', errorResource);
            // Set default options if API fails
            this.segmentOptions = ['Retail', 'Premium', 'VIP'];
            this.wealthMembershipOptions = ['Standard', 'Wealth', 'Wealth Plus', 'Elite'];
            resolve({ status: 0, data: { segmentOptions: this.segmentOptions, wealthMembershipOptions: this.wealthMembershipOptions } });
          }
        );
      });
    },

    // Handle selection in the Action dropdown, including the appended "Add Action" entry
    handleActionSelect(feature, value) {
      if (value === this.ADD_ACTION_VALUE) {
        this.showSuggestionActionManager = true;
        return;
      }
      feature.action = value;
    },

    // Load suggestion action options (IsTaxPlanningMode = true)
    async GetSuggestionActions() {
      return new Promise((resolve) => {
        this.$call.GetTaxPlanningSuggestionActions(
          (dataResponse) => {
            this.actionOptions = (dataResponse && dataResponse.data) || [];
            resolve(dataResponse);
          },
          (errorResource) => {
            console.error('Error getting suggestion actions:', errorResource);
            this.actionOptions = [];
            resolve({ status: 0, data: [] });
          }
        );
      });
    },

    // Date formatting methods (similar to ProactiveMessage)
    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD/MM/YYYY');
    },

    formatDateForAPI(dateString) {
      if (!dateString) return null;
      return moment(dateString).format('YYYY-MM-DD');
    },

    formatDateForInput(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('YYYY-MM-DD');
    },

    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }

      this.today = dd + '/' + mm + '/' + yyyy;

      // Initialize date picker with current date
      this.endDate = yyyy + '-' + (mm < 10 ? '0' + mm : mm) + '-' + (dd < 10 ? '0' + dd : dd);

      // Set start date to beginning of current month
      this.startDate = yyyy + '-' + (mm < 10 ? '0' + mm : mm) + '-01';

      // Initialize selected dates array
      this.selectedDates = [this.startDate, this.endDate];
    },
    updateDateRange(dates) {
      if (dates && dates.length > 0) {
        // Sort dates to ensure proper start and end
        const sortedDates = dates.sort((a, b) => new Date(a) - new Date(b));
        this.startDate = sortedDates[0];
        this.endDate = sortedDates[sortedDates.length - 1];

        // If only one date selected, use it as both start and end
        if (sortedDates.length === 1) {
          this.endDate = this.startDate;
        }

        // Close menu after selection of 2 dates
        if (sortedDates.length >= 2) {
          this.datePickerMenu = false;
        }
      }
    },
    formatDisplayDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    },
    validatePriority(event, item) {
      // Only allow digits 0-9
      const value = event.target ? event.target.value : event;
      const numericValue = value.replace(/[^0-9]/g, '');
      item.priority = numericValue;
    },

    // ============ CIF File Management Methods ============

    // Download CIF Template
    downloadCifTemplate() {
      const csvContent = 'cif\n12345678901\n23456789012\n34567890123';
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'cif_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$swal.fire({
          icon: 'success',
          title: 'Template Downloaded',
          text: 'CIF template file has been downloaded successfully!',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    },

    // Feature CIF Upload Methods
    triggerFeatureCifUpload(index) {
      this.currentFeatureIndexForCif = index;
      this.$refs.featureCifInput.click();
    },

    handleFeatureCifUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const featureIndex = this.currentFeatureIndexForCif;
      if (featureIndex === null || featureIndex === undefined) return;

      const feature = this.suggestionConfig.features[featureIndex];
      if (!feature) return;

      // Validate file type
      const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type)) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Invalid File Type',
          text: 'Please upload only CSV or Excel files',
        });
        return;
      }

      // Validate file size (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal.fire({
          icon: 'warning',
          title: 'File Too Large',
          text: 'File size should not exceed 5MB',
        });
        return;
      }

      // Initialize cifFiles array if needed
      if (!feature.cifFiles) {
        feature.cifFiles = [];
      }

      // Check for duplicate file name
      const existingFile = feature.cifFiles.find((f) => f.fileName === file.name);
      if (existingFile) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Duplicate File',
          text: 'A file with this name has already been uploaded. Please rename the file or remove the existing one.',
        });
        return;
      }

      // Upload file to server
      const formData = new FormData();
      formData.append('file', file);

      this.$call.UploadTaxPlanningCif(
        formData,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            const newCifFile = {
              fileName: dataResponse.data.fileName || file.name,
              filePath: dataResponse.data.fileUrl,
              cifList: dataResponse.data.cifList || [],
              uploadDate: new Date(),
              fileSize: file.size,
            };

            feature.cifFiles.push(newCifFile);

            this.$swal.fire({
              icon: 'success',
              title: 'Success',
              text: `File uploaded successfully. Found ${dataResponse.data.cifCount || 0} CIF codes.`,
              timer: 2000,
              showConfirmButton: false,
            });

            // Reset file input
            this.$refs.featureCifInput.value = '';
            this.currentFeatureIndexForCif = null;
          } else {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: dataResponse.message || 'Failed to upload file',
            });
          }
        },
        (errorResource) => {
          console.error('Error uploading file:', errorResource);
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorResource.response?.data?.error || 'Failed to upload file',
          });
        }
      );
    },

    removeFeatureCifFile(featureIndex, fileIndex) {
      const fileName = this.suggestionConfig.features[featureIndex].cifFiles[fileIndex].fileName;
      this.suggestionConfig.features[featureIndex].cifFiles.splice(fileIndex, 1);

      this.$swal.fire({
        icon: 'info',
        title: 'File Removed',
        html: `"<strong>${fileName}</strong>" removed.<br><small style="color: #ff9800;">⚠️ Remember to click Save to confirm changes.</small>`,
        timer: 3500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
      });
    },

    clearAllFeatureCifFiles(featureIndex) {
      const feature = this.suggestionConfig.features[featureIndex];
      const fileCount = feature.cifFiles.length;
      feature.cifFiles = [];

      this.$swal.fire({
        icon: 'info',
        title: 'All Files Removed',
        html: `<strong>${fileCount} files</strong> removed.<br><small style="color: #ff9800;">⚠️ Remember to click Save to confirm changes.</small>`,
        timer: 3500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
      });
    },

    downloadFeatureCifFile(file) {
      if (file && file.filePath) {
        const link = document.createElement('a');
        link.href = file.filePath;
        link.download = file.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$swal.fire({
          icon: 'success',
          title: 'Downloading',
          text: 'File download started successfully!',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No file available for download',
        });
      }
    },

    getTotalFeatureCifCodes(feature) {
      if (!feature.cifFiles || feature.cifFiles.length === 0) return 0;
      return feature.cifFiles.reduce((total, file) => {
        return total + (file.cifList ? file.cifList.length : 0);
      }, 0);
    },

    // Question CIF Upload Methods
    triggerQuestionCifUpload(index) {
      this.currentQuestionIndexForCif = index;
      this.$refs.questionCifInput.click();
    },

    handleQuestionCifUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const questionIndex = this.currentQuestionIndexForCif;
      if (questionIndex === null || questionIndex === undefined) return;

      const question = this.suggestionQuestionConfig.questions[questionIndex];
      if (!question) return;

      // Validate file type
      const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type)) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Invalid File Type',
          text: 'Please upload only CSV or Excel files',
        });
        return;
      }

      // Validate file size (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal.fire({
          icon: 'warning',
          title: 'File Too Large',
          text: 'File size should not exceed 5MB',
        });
        return;
      }

      // Initialize cifFiles array if needed
      if (!question.cifFiles) {
        question.cifFiles = [];
      }

      // Check for duplicate file name
      const existingFile = question.cifFiles.find((f) => f.fileName === file.name);
      if (existingFile) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Duplicate File',
          text: 'A file with this name has already been uploaded. Please rename the file or remove the existing one.',
        });
        return;
      }

      // Upload file to server
      const formData = new FormData();
      formData.append('file', file);

      this.$call.UploadTaxPlanningCif(
        formData,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            const newCifFile = {
              fileName: dataResponse.data.fileName || file.name,
              filePath: dataResponse.data.fileUrl,
              cifList: dataResponse.data.cifList || [],
              uploadDate: new Date(),
              fileSize: file.size,
            };

            question.cifFiles.push(newCifFile);

            this.$swal.fire({
              icon: 'success',
              title: 'Success',
              text: `File uploaded successfully. Found ${dataResponse.data.cifCount || 0} CIF codes.`,
              timer: 2000,
              showConfirmButton: false,
            });

            // Reset file input
            this.$refs.questionCifInput.value = '';
            this.currentQuestionIndexForCif = null;
          } else {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: dataResponse.message || 'Failed to upload file',
            });
          }
        },
        (errorResource) => {
          console.error('Error uploading file:', errorResource);
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorResource.response?.data?.error || 'Failed to upload file',
          });
        }
      );
    },

    removeQuestionCifFile(questionIndex, fileIndex) {
      const fileName = this.suggestionQuestionConfig.questions[questionIndex].cifFiles[fileIndex].fileName;
      this.suggestionQuestionConfig.questions[questionIndex].cifFiles.splice(fileIndex, 1);

      this.$swal.fire({
        icon: 'info',
        title: 'File Removed',
        html: `"<strong>${fileName}</strong>" removed.<br><small style="color: #ff9800;">⚠️ Remember to click Save to confirm changes.</small>`,
        timer: 3500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
      });
    },

    clearAllQuestionCifFiles(questionIndex) {
      const question = this.suggestionQuestionConfig.questions[questionIndex];
      const fileCount = question.cifFiles.length;
      question.cifFiles = [];

      this.$swal.fire({
        icon: 'info',
        title: 'All Files Removed',
        html: `<strong>${fileCount} files</strong> removed.<br><small style="color: #ff9800;">⚠️ Remember to click Save to confirm changes.</small>`,
        timer: 3500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
      });
    },

    downloadQuestionCifFile(file) {
      if (file && file.filePath) {
        const link = document.createElement('a');
        link.href = file.filePath;
        link.download = file.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$swal.fire({
          icon: 'success',
          title: 'Downloading',
          text: 'File download started successfully!',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No file available for download',
        });
      }
    },

    getTotalQuestionCifCodes(question) {
      if (!question.cifFiles || question.cifFiles.length === 0) return 0;
      return question.cifFiles.reduce((total, file) => {
        return total + (file.cifList ? file.cifList.length : 0);
      }, 0);
    },

    // Helper methods for file display
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatUploadDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    // Load GPT Models for dropdown
    loadGPTModels() {
      return new Promise((resolve, reject) => {
        this.$call.DDGPTModel(
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.aiModelOptions = dataResponse.data.map((m) => ({
                name: m.name,
                value: m.modelDisplayID,
                modelId: m.id,
                icon: 'mdi-robot',
                color: '#10a37f',
                searchableText: `${m.modelDisplayID}: ${m.name}`,
              }));
              this.uniqueAiModelOptions = this.aiModelOptions.filter((option, index, self) => index === self.findIndex((t) => t.value === option.value));
            } else {
              this.aiModelOptions = [];
            }
            resolve();
          },
          (err) => {
            this.aiModelOptions = [];
            reject(err);
          }
        );
      });
    },

    // Title shown for a voice configuration option, e.g. "#1 — LHB Production"
    voiceConfigItemTitle(item) {
      if (!item) return '';
      return `#${item.displayId} — ${item.name}`;
    },

    // Load the voice settings bundles managed in LH Bank Configuration › Voice Configs
    loadVoiceConfigurations() {
      return new Promise((resolve) => {
        this.$call.GetDDVoiceConfiguration(
          (dataResponse) => {
            if (dataResponse && dataResponse.status === 0) {
              this.voiceConfigOptions = dataResponse.data || [];
            } else {
              console.error('Failed to load voice configurations:', dataResponse);
              this.voiceConfigOptions = [];
            }
            resolve();
          },
          (errorResource) => {
            console.error('Error getting voice configurations:', errorResource);
            this.voiceConfigOptions = [];
            resolve();
          }
        );
      });
    },
  },

  async mounted() {
    // Check if call API is available
    if (!this.$call) {
      console.error('Call API is not available. Please ensure it is properly configured.');
      this.$swal.fire({
        title: this.$t('error'),
        text: 'Application not properly configured. Please refresh the page.',
        icon: 'error',
        confirmButtonText: this.$t('ok'),
      });
      return;
    }

    // Load dropdown options first
    await this.GetOptions();
    await this.GetSuggestionActions();
    await this.loadGPTModels();
    await this.loadVoiceConfigurations();

    await this.loadConfiguration();
    this.GetDate();
  },
};
</script>

<style scoped>
.gradient-bar {
  height: 1px;
  width: auto;
  background: linear-gradient(90deg, #d56796 0%, #345296 100%);
}

.language-option {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.language-config-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.language-config-subtitle {
  font-size: 18px;
  color: #666;
  margin-bottom: 20px;
}

.language-switches {
  padding-left: 0;
}

.voice-sub-options {
  margin-left: 32px;
  margin-top: 12px;
  padding-left: 16px;
  border-left: 2px solid #f0f0f0;
}

.voice-system-prompt-section {
  background: linear-gradient(135deg, rgba(78, 57, 130, 0.05) 0%, rgba(52, 82, 150, 0.05) 100%);
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid #4e3982;
}

.voice-prompt-label {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  letter-spacing: 0.3px;
}

.voice-prompt-textarea {
  border: 1px solid #e0e0e0;
  padding: 0px 5px 5px 15px;
  background-color: #ffffff;
  border-radius: 8px;
}

.voice-prompt-textarea :deep(.v-field) {
  border: none !important;
  box-shadow: none !important;
}

/* App Header Section in Mobile Preview */
.app-header-section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  margin-bottom: 0;
}

.app-header-section .v-icon {
  transition: opacity 0.2s ease;
}

.app-header-section .v-icon:hover {
  opacity: 1 !important;
}

.mobile-preview-container {
  position: sticky;
  top: 20px;
}

.mobile-mockup {
  width: 280px;
  height: 580px;
  background: linear-gradient(135deg, #5a9fd7 0%, #4a8bc2 100%);
  border-radius: 30px;
  padding: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), 0 4px 16px rgba(90, 159, 215, 0.3);
}

.mobile-screen {
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.mobile-header {
  background: linear-gradient(135deg, #5a9fd7 0%, #4a8bc2 100%);
  color: white;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 4px;
  font-size: 14px;
  font-weight: 600;
}

.status-icons {
  display: flex;
  gap: 4px;
}

.time {
  font-weight: 600;
  font-size: 14px;
}

.app-header {
  display: flex;
  align-items: center;
  padding: 8px 16px 12px;
}

.header-title-mobile {
  font-size: 16px;
  font-weight: 600;
  margin-left: 8px;
  color: white;
}

.chat-content {
  flex: 1;
  background: linear-gradient(180deg, #2d3748 0%, #1a202c 100%);
  position: relative;
}

.language-flags {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.flag-option {
  cursor: pointer;
  transition: transform 0.2s;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.flag-option:hover {
  transform: scale(1.1);
}

.input-area {
  background: white;
  padding: 12px 16px 16px;
  border-top: 1px solid #e0e0e0;
}

.input-container {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 25px;
  padding: 10px 16px;
  gap: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.message-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #666;
}

.message-input::placeholder {
  color: #adb5bd;
  font-size: 14px;
}

.voice-icon,
.send-icon {
  cursor: pointer;
  transition: color 0.2s;
}

.voice-icon:hover,
.send-icon:hover {
  color: #4a8bc2 !important;
}

.bs {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.header-title {
  color: white;
}

.mobile-preview-simulator {
  width: 268px;
  height: 582px;
  border-radius: 40px;
  overflow: hidden;
  position: relative;
  background-image: url('/lhb/backgroundmock.png');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.sug-simulator {
  width: 268px;
  height: 582px;
  border-radius: 40px;
  overflow: hidden;
  position: relative;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.sug-simulator-bg {
  background-image: url('/lhb/backgroundmock.png');
}

.sug-simulator-bg-without-mic {
  background-image: url('/lhb/backgroundmock.png');
}

.feature-card {
  width: 48px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.feature-card:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.question-card:hover {
  transform: scale(1.02);
  background: rgba(70, 57, 130, 0.9) !important;
}

.sug-quest-simulator {
  width: 268px;
  height: 582px;
  border-radius: 40px;
  overflow: hidden;
  position: relative;
  background-image: url('/lhb/sug_feat.png');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}

.feature-card-static {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.grow {
  flex-grow: 1;
}

.grow-0 {
  flex-grow: 0;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.feature-section {
  background-color: #f3f3f3;
  border-radius: 16px !important;
  border: none !important;
}

.suggestion-form .v-input--radio-group--row .v-radio {
  margin-right: 16px;
}

.suggestion-form .v-input--radio-group--row .v-input--radio-group__input {
  margin-bottom: 0;
}

/* Skeleton Loader Styles */
.mobile-skeleton {
  border-radius: 40px !important;
  overflow: hidden;
}

.mobile-skeleton .v-skeleton-loader__image {
  border-radius: 40px;
}

/* Loading States */
.skeleton-container {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* Smooth transitions when content loads */
.configuration-content {
  transition: opacity 0.3s ease-in-out;
}

.configuration-content.loading {
  opacity: 0;
}

.configuration-content.loaded {
  opacity: 1;
}

/* Vue Advanced Cropper CSS - Custom styling for 1:1 aspect ratio cropping */
.crop-container {
  position: relative;
  max-width: 100%;
  max-height: 400px;
  overflow: hidden;
  background: #f8f9fa;
  border-radius: 8px;
}

.cropper {
  height: 400px;
  width: 100%;
}

/* Custom theme colors to match app */
.vue-advanced-cropper__stencil {
  border: 2px solid #4e3982 !important;
}

.vue-advanced-cropper__handler {
  background: #4e3982 !important;
  border: 2px solid #4e3982 !important;
}

.vue-advanced-cropper__line {
  background: #4e3982 !important;
}

.vue-advanced-cropper__background {
  background: rgba(0, 0, 0, 0.5) !important;
}

/* Field label styling */
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
}

/* Custom input styling */
.custom-input {
  width: 100%;
  height: 45px;
  padding: 8px 16px;
  border: 1px solid #bec2ca;
  border-radius: 16px;
  background-color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
}

.custom-input:focus {
  border-color: #643990;
  outline: none;
}

.custom-input::placeholder {
  color: #9e9e9e;
}

/* Error styling for inputs */
.custom-input.input-error {
  border-color: #f44336;
  background-color: #ffebee;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
}

/* Custom select styling */
.custom-select :deep(.v-field) {
  border-radius: 16px !important;
  background-color: white;
  height: 45px;
  border: 1px solid #bec2ca;
  box-shadow: none !important;
}

.custom-select :deep(.v-field--focused) {
  border-color: #643990;
}

.custom-select :deep(.v-field__input) {
  padding: 8px 16px;
  font-size: 14px;
  min-height: auto;
  padding-left: 16px !important;
  height: 100% !important;
}

.custom-select :deep(.v-select__selection-text) {
  font-size: 14px;
}

.custom-select :deep(.v-field__append-inner) {
  padding-right: 0px;
}

.custom-select :deep(.v-field__overlay) {
  display: none;
}

/* Custom select dropdown menu styling */
.custom-select :deep(.v-overlay__content) {
  border-radius: 16px !important;
  overflow: hidden;
}

.custom-select :deep(.v-list) {
  border-radius: 16px !important;
  padding: 8px 0;
}

.custom-select :deep(.v-list-item) {
  padding: 8px 16px;
  min-height: 40px;
}

.custom-select :deep(.v-list-item:hover) {
  background-color: #f5f5f5;
}

.custom-select :deep(.v-list-item--active) {
  background-color: #f0e6f6 !important;
  color: #643990 !important;
}

.custom-select :deep(.v-list-item__overlay) {
  display: none !important;
}

.custom-select :deep(.v-list-item .v-list-item__overlay) {
  opacity: 0 !important;
}

.SuggestFeatureIcon {
  background: linear-gradient(135deg, #4c4f98 30%, #191a32 100%) !important;
  border-radius: 12px;
  border: solid 2px #bcbec0;
  padding: 6px;
  height: 90px;
  width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Allow sticky to work: v-card, v-window and v-window-item have overflow:hidden
   by default which blocks sticky. Safe to remove since we disabled the slide transition
   and border-radius clipping is not needed here. */
.bs {
  overflow: visible !important;
}

:deep(.v-window),
:deep(.v-window__container),
:deep(.v-window-item) {
  overflow: visible !important;
}

/* Preview column must STRETCH (not flex-start) so the containing block is as tall
   as the form column — sticky needs a tall parent to have scroll range to work with */
.split-preview-col {
  align-self: stretch;
}

.mobile-preview-sticky {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  scrollbar-width: none;
}

.mobile-preview-sticky::-webkit-scrollbar {
  display: none;
}
.postMsg {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

/* Model Selector Styles */
.model-option {
  padding: 8px 0;
}

.model-icon-small {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modern Input Styles for Model Dropdown */
.modern-input :deep(.v-field) {
  border-radius: 14px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.modern-input :deep(.v-field--focused) {
  transform: translateY(-1px);
}

.modern-input :deep(.v-field__outline) {
  --v-field-border-opacity: 0.15;
}

.modern-input :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
  color: rgb(102, 126, 234) !important;
  --v-field-border-width: 2px;
}

.modern-input :deep(.v-label) {
  font-weight: 500;
}

/* Cache Config Section Styles */
.cache-config-section {
  padding: 16px;
  background: #fafafa;
  border-radius: 12px;
  margin-bottom: 16px;
}

.cache-section-label {
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-left: 12px;
  border-left: 3px solid #1976d2;
}

.switch-option-compact {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.switch-option-compact:hover {
  border-color: #bdbdbd;
}

.switch-active-bg {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.04) 100%);
}

.switch-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.switch-description {
  font-size: 11.5px;
  color: #757575;
}

.cache-config-input :deep(.v-field) {
  border-radius: 12px !important;
}

.slider-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}

/* Genie AI Character Styles */
.genie-icon-upload-box {
  width: 100%;
  aspect-ratio: 1;
  border: 2px dashed #ccc;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background-color: #fafafa;
}

.genie-icon-upload-box:hover {
  border-color: #4E3982;
  background-color: #f0ecf6;
}

.genie-icon-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.genie-preview-container {
  width: 300px;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.genie-preview-card {
  width: 100%;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.genie-preview-empty {
  width: 100%;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.genie-preview-label {
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
}

.genie-preview-icon-frame {
  width: 100%;
  aspect-ratio: 1;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #fafafa;
}

/* Tax Planning Mode radio options (Main Configuration tab) */
.tax-mode-option {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}

.tax-mode-title {
  font-weight: 500;
  color: #333;
}

.tax-mode-desc {
  font-size: 12px;
  color: #666;
}
</style>
